import React, { useState, useEffect, useLayoutEffect, useRef, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import axiosInstance from '../config/axios';
import logger from '../utils/logger';
import './CountSubmission.css';
import { useCountOfflineDrafts } from '../offline/useCountOfflineDrafts';
import { useFinalSubmissionQueue } from '../offline/useFinalSubmissionQueue';
import { subscribeFinalSubmissionSync } from '../offline/finalSubmissionSync';
import CountSyncStatusStrip from './CountSyncStatusStrip';
import PendingFinalSubmissionModal from './PendingFinalSubmissionModal';
import FinalSubmissionSuccessModal from './FinalSubmissionSuccessModal';
import { friendlyItemLabel, compareItemsByFriendlyDisplay, inventoryTextSearchScore, inventoryNotificationLabel } from '../utils/inventoryDisplay';
import {
  FLAG_HIGH_VALUE,
  FLAG_GLOBAL_VALUE_THRESHOLD,
  computeReviewFlags,
  buildThresholdContextForSite,
  submissionNoteRequiredFromFlags,
  flagTypeLabels
} from '../utils/countReviewFlags';
import {
  normalizePeriodKey,
  countSubmissionStorageKey,
  legacyCountSubmissionStorageKey,
  isLocalDraftStaleForPeriod,
  isServerDraftStaleForPeriod
} from '../offline/countDraftConstants';
import {
  PENDING_FINAL_SUBMISSION_COPY,
  handleCheckPendingFinalProgress,
  isPendingFinalSubmissionUiEligible,
  isPendingFinalSubmissionStatus,
  isRecalledFinalSubmissionEditing,
  pendingFinalSubmissionKey,
  shouldOpenPendingFinalSubmissionModal
} from '../offline/pendingFinalSubmissionUi';
import { handleFinalSubmissionSuccessRefresh } from '../offline/finalSubmissionSuccessUi';
import { getFriendlyError } from '../utils/errorMessages';

const CountSubmission = ({ sites, inventory: initialInventory, onSubmitCount, initialSiteId, onSiteSelected, isSiteLogin = false, forceReload = 0, siteName = '', onOpenMyCounts }) => {
  const { user } = useAuth();
  const [inventory, setInventory] = useState(initialInventory || []);
  const [globalInventoryValueLimit, setGlobalInventoryValueLimit] = useState(null);
  const [globalInventoryValueLimitsByCluster, setGlobalInventoryValueLimitsByCluster] = useState({});
  const [selectedSiteId, setSelectedSiteId] = useState(isSiteLogin && initialSiteId ? initialSiteId : '');
  const [personName, setPersonName] = useState(isSiteLogin ? '' : (user?.name || ''));
  const [generalNotes, setGeneralNotes] = useState('');
  const [counts, setCounts] = useState({}); // { itemId: { quantity: 0, quantity2: 0, notes: '' } }
  const [locationFilter, setLocationFilter] = useState(''); // Filter by location
  const [searchTerm, setSearchTerm] = useState(''); // Search term for filtering inventory
  const [sortColumn, setSortColumn] = useState('category'); // Column to sort: name|category|packSize|unit|countedQty|unit2|countedQty2|location|priority|''
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' | 'desc'
  const [error, setError] = useState('');
  const [errorAction, setErrorAction] = useState(null); // { label, kind: 'refresh'|'my-counts'|'retry' }
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showReview, setShowReview] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [recalledBatchId, setRecalledBatchId] = useState(null); // Track if we're editing a recalled/rejected count
  const [editingStatus, setEditingStatus] = useState(null); // Track if editing recalled or rejected count
  const [showPriorityConfirmation, setShowPriorityConfirmation] = useState(false); // Show confirmation for zero-count priority items
  const [priorityZeroItems, setPriorityZeroItems] = useState([]); // List of priority items with zero counts
  const [countingPeriodStartedAt, setCountingPeriodStartedAt] = useState(null); // Server counting period; when it changes, local drafts are invalid
  const [countingPeriodReady, setCountingPeriodReady] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false); // Track if draft is being saved
  const [preparingReview, setPreparingReview] = useState(false); // Auto-save draft before Review Count
  const [missingCommentModal, setMissingCommentModal] = useState(null); // { count, firstInventoryItemId } or null
  const [connectionRequiredModalOpen, setConnectionRequiredModalOpen] = useState(false);
  const [pendingFinalModalOpen, setPendingFinalModalOpen] = useState(false);
  const [pendingFinalBannerVisible, setPendingFinalBannerVisible] = useState(false);
  const [finalSubmissionSuccessModalOpen, setFinalSubmissionSuccessModalOpen] = useState(false);
  const [confirmedPendingSubmissionKey, setConfirmedPendingSubmissionKey] = useState(null);
  const [recallStateResolved, setRecallStateResolved] = useState(false);
  const [stickyHeaderTop, setStickyHeaderTop] = useState(52);
  const missingCommentModalPrimaryRef = useRef(null);
  const connectionModalOkRef = useRef(null);
  const connectionModalReturnFocusRef = useRef('review'); // 'review' | 'submit'
  const pendingFinalDismissedKeyRef = useRef(null);
  const pendingFinalOpenedKeyRef = useRef(null);
  const reviewCountBtnRef = useRef(null);
  const submitToManagerBtnRef = useRef(null);
  const toolbarRef = useRef(null);
  const countingPeriodFetchedRef = useRef(false);
  const prevInitialSiteIdRef = useRef(null);
  const prevForceReloadRef = useRef(0);
  const isRestoringFromStorageRef = useRef(false);
  const reviewNotesInputRefs = useRef({});
  const reviewPanelRef = useRef(null);
  const [reviewUseCardLayout, setReviewUseCardLayout] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= 767
  );
  const draftLoadedRef = useRef(false); // Track if we've loaded draft from server
  const offlineDraft = useCountOfflineDrafts({
    user,
    selectedSiteId,
    countingPeriodStartedAt,
    countingPeriodFetched: countingPeriodReady,
    counts,
    personName,
    generalNotes,
    recalledBatchId,
    inventory,
    isRestoringFromStorageRef
  });
  const finalSubmission = useFinalSubmissionQueue({
    user,
    selectedSiteId,
    countingPeriodStartedAt,
    countingPeriodFetched: countingPeriodReady,
    processingEnabled: recallStateResolved
  });
  const {
    retryFinalSubmission,
    refreshFinalSubmission
  } = finalSubmission;
  const recalledFinalSubmissionEditing = isRecalledFinalSubmissionEditing({
    recalledBatchId,
    editingStatus
  });
  const pendingFinalSubmissionEligible = isPendingFinalSubmissionUiEligible({
    row: finalSubmission.pendingRow,
    successModalOpen: finalSubmissionSuccessModalOpen,
    queueBusy: finalSubmission.busy,
    submitting,
    recallStateResolved,
    recalledEditing: recalledFinalSubmissionEditing,
    recalledBatchId,
    browserOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
    confirmedSubmissionKey: confirmedPendingSubmissionKey
  });
  const finalSubmissionRowIsPending = isPendingFinalSubmissionStatus(
    finalSubmission.pendingRow && finalSubmission.pendingRow.status
  );
  const showFinalSubmissionStatus =
    finalSubmission.submissionUi &&
    finalSubmission.submissionUi.headline &&
    (!finalSubmissionRowIsPending || pendingFinalSubmissionEligible);

  const openPendingFinalSubmissionModal = useCallback((rowOrKey) => {
    const key =
      typeof rowOrKey === 'string' || rowOrKey == null
        ? rowOrKey
        : pendingFinalSubmissionKey(rowOrKey);
    const submissionKey = key || 'pending';
    setPendingFinalBannerVisible(true);
    if (pendingFinalModalOpen) {
      // Upgrade placeholder key once IndexedDB row is known; do not re-open.
      if (key) pendingFinalOpenedKeyRef.current = key;
      return;
    }
    if (
      !shouldOpenPendingFinalSubmissionModal({
        isQueuedPending: true,
        submissionKey,
        dismissedKey: pendingFinalDismissedKeyRef.current,
        alreadyOpen: false
      })
    ) {
      return;
    }
    pendingFinalOpenedKeyRef.current = submissionKey;
    setPendingFinalModalOpen(true);
  }, [pendingFinalModalOpen]);

  const dismissPendingFinalSubmissionModal = useCallback(() => {
    pendingFinalDismissedKeyRef.current =
      pendingFinalOpenedKeyRef.current || pendingFinalDismissedKeyRef.current || 'pending';
    setPendingFinalModalOpen(false);
    setPendingFinalBannerVisible(true);
  }, []);

  const showFinalSubmissionSuccessModal = useCallback(() => {
    setPendingFinalModalOpen(false);
    setPendingFinalBannerVisible(false);
    setConfirmedPendingSubmissionKey(null);
    pendingFinalDismissedKeyRef.current = null;
    pendingFinalOpenedKeyRef.current = null;
    setError('');
    setErrorAction(null);
    setSuccess('');
    setShowPriorityConfirmation(false);
    setFinalSubmissionSuccessModalOpen(true);
  }, []);

  const handleFinalSubmissionSuccessConfirm = useCallback(() => {
    if (onSubmitCount) {
      void onSubmitCount({ success: true });
    }
    handleFinalSubmissionSuccessRefresh();
  }, [onSubmitCount]);

  // When a queued final submission syncs in the background, replace pending UX with success.
  useEffect(() => {
    return subscribeFinalSubmissionSync((ev) => {
      if (ev.type !== 'submitted' || !ev.row) return;
      if (selectedSiteId && ev.row.siteId !== selectedSiteId) return;
      if (recalledFinalSubmissionEditing) {
        const eventRecalledBatchId =
          ev.row.payload && ev.row.payload.recalledBatchId
            ? ev.row.payload.recalledBatchId
            : null;
        if (!recalledBatchId || eventRecalledBatchId !== recalledBatchId) return;
      }
      showFinalSubmissionSuccessModal();
    });
  }, [
    recalledBatchId,
    recalledFinalSubmissionEditing,
    selectedSiteId,
    showFinalSubmissionSuccessModal
  ]);

  // Show pending popup once when a local final submission is queued / still pending.
  useEffect(() => {
    const row = finalSubmission.pendingRow;
    if (!pendingFinalSubmissionEligible) {
      if (!row) {
        setConfirmedPendingSubmissionKey(null);
      }
      if (
        !row ||
        finalSubmissionSuccessModalOpen ||
        recalledFinalSubmissionEditing ||
        !recallStateResolved
      ) {
        setPendingFinalBannerVisible(false);
        setPendingFinalModalOpen(false);
      }
      return;
    }
    setPendingFinalBannerVisible(true);
    const key = pendingFinalSubmissionKey(row);
    if (!key) return;
    if (pendingFinalDismissedKeyRef.current === key) return;
    if (pendingFinalOpenedKeyRef.current === key) return;
    // Same popup session started with a placeholder key before the row refreshed.
    if (pendingFinalModalOpen && pendingFinalOpenedKeyRef.current === 'pending') {
      pendingFinalOpenedKeyRef.current = key;
      return;
    }
    openPendingFinalSubmissionModal(row);
  }, [
    finalSubmission.pendingRow,
    finalSubmissionSuccessModalOpen,
    pendingFinalSubmissionEligible,
    pendingFinalModalOpen,
    openPendingFinalSubmissionModal,
    recalledFinalSubmissionEditing,
    recallStateResolved
  ]);

  // Soft auto-check while the pending popup is visible (does not replace Check progress).
  useEffect(() => {
    if (!pendingFinalModalOpen) return undefined;
    const tick = () => {
      if (typeof retryFinalSubmission === 'function') {
        void retryFinalSubmission();
      } else if (typeof refreshFinalSubmission === 'function') {
        void refreshFinalSubmission();
      }
    };
    const t = window.setInterval(tick, 12000);
    return () => window.clearInterval(t);
  }, [
    pendingFinalModalOpen,
    retryFinalSubmission,
    refreshFinalSubmission
  ]);


  // Get the localStorage key for this user/site/current server counting period.
  const getStorageKey = (siteIdOverride = null) => {
    const userId = isSiteLogin ? user?.id || 'site' : user?.id || 'user';
    const siteId = siteIdOverride || selectedSiteId || 'none';
    return countSubmissionStorageKey(userId, siteId, countingPeriodStartedAt);
  };

  // Save form data to localStorage
  const saveToLocalStorage = () => {
    if (!countingPeriodFetchedRef.current) {
      logger.log('Skipping save - counting period not loaded');
      return;
    }
    // Don't save if we're currently restoring from localStorage
    if (isRestoringFromStorageRef.current) {
      logger.log('Skipping save - currently restoring from localStorage');
      return;
    }
    // Don't save if user is not loaded yet
    if (!user || !user.id) {
      logger.log('Skipping save - user not loaded yet');
      return;
    }
    // Don't save if no site is selected
    if (!selectedSiteId) {
      logger.log('Skipping save - no site selected');
      return;
    }
    try {
      const storageKey = getStorageKey();
      const dataToSave = {
        counts,
        personName,
        generalNotes,
        selectedSiteId,
        timestamp: Date.now(),
        periodStartedAt: countingPeriodStartedAt // So we can invalidate when admin starts new period
      };
      localStorage.setItem(storageKey, JSON.stringify(dataToSave));
      logger.log('âœ… Saved to localStorage:', { 
        storageKey, 
        countsCount: Object.keys(counts).length,
        userId: user.id,
        siteId: selectedSiteId
      });
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };


  // Clear count-form localStorage for one site across periods. This never touches
  // the IndexedDB final-submission queue.
  const clearLocalStorage = (siteId = null) => {
    try {
      const userId = isSiteLogin ? user?.id || 'site' : user?.id || 'user';
      const targetSiteId = siteId || selectedSiteId || 'none';
      const prefix = `${legacyCountSubmissionStorageKey(userId, targetSiteId)}_`;
      const legacyKey = legacyCountSubmissionStorageKey(userId, targetSiteId);
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key === legacyKey || (key && key.startsWith(prefix))) {
          localStorage.removeItem(key);
        }
      }
      logger.log('âœ… Cleared period-scoped drafts from localStorage:', { userId, siteId: targetSiteId });
    } catch (error) {
      console.error('Error clearing localStorage:', error);
    }
  };

  const loadCurrentPeriodLocalDraft = (userId, siteId) => {
    if (!countingPeriodFetchedRef.current) return null;
    const storageKey = countSubmissionStorageKey(userId, siteId, countingPeriodStartedAt);
    const legacyKey = legacyCountSubmissionStorageKey(userId, siteId);
    let sourceKey = storageKey;
    let savedData = localStorage.getItem(storageKey);
    if (!savedData) {
      sourceKey = legacyKey;
      savedData = localStorage.getItem(legacyKey);
    }
    if (!savedData) return null;

    const parsed = JSON.parse(savedData);
    if (isLocalDraftStaleForPeriod(parsed, countingPeriodStartedAt)) {
      localStorage.removeItem(sourceKey);
      return null;
    }

    if (sourceKey === legacyKey) {
      localStorage.setItem(storageKey, savedData);
      localStorage.removeItem(legacyKey);
    }
    return parsed;
  };

  const fetchInventoryForSite = async (siteId) => {
    setRecallStateResolved(false);
    setPendingFinalModalOpen(false);
    setPendingFinalBannerVisible(false);
    setConfirmedPendingSubmissionKey(null);

    const lineHasUserData = (c) =>
      c &&
      ((c.quantity !== '' && c.quantity !== undefined && c.quantity !== null) ||
        (c.quantity2 !== '' && c.quantity2 !== undefined && c.quantity2 !== null) ||
        (c.notes && String(c.notes).trim() !== ''));

    const tryOfflinePersistenceFallback = async (sId, inventoryList) => {
      if (!user?.id) return;
      const invList = inventoryList || [];
      const mergedCounts = {};
      invList.forEach((item) => {
        mergedCounts[item.id] = { quantity: '', quantity2: '', notes: '' };
      });
      let metaPn = '';
      let metaGn = '';
      if (offlineDraft.enabled && offlineDraft.loadDexieSnapshotForInventory) {
        try {
          const dex = await offlineDraft.loadDexieSnapshotForInventory(invList);
          if (dex && dex.hasData) {
            Object.assign(mergedCounts, dex.counts);
            metaPn = dex.personName || '';
            metaGn = dex.generalNotes || '';
          }
        } catch (e) {
          console.warn('loadDexieSnapshotForInventory', e);
        }
      }
      // Wait for counting period before touching localStorage — avoids restoring stale drafts
      if (!countingPeriodFetchedRef.current) {
        const hasLineEarly = invList.some((it) => lineHasUserData(mergedCounts[it.id]));
        if (hasLineEarly || metaPn || metaGn) {
          if (metaPn) setPersonName(metaPn);
          if (metaGn) setGeneralNotes(metaGn);
          isRestoringFromStorageRef.current = true;
          setCounts(mergedCounts);
          logger.log('Restored from IndexedDB only (period not ready for localStorage)');
          setTimeout(() => {
            isRestoringFromStorageRef.current = false;
          }, 800);
        }
        return;
      }
      try {
        const parsed = loadCurrentPeriodLocalDraft(user.id, sId);
        if (parsed && parsed.selectedSiteId === sId && parsed.counts) {
          Object.keys(parsed.counts).forEach((itemId) => {
            if (!mergedCounts[itemId]) return;
            const sc = parsed.counts[itemId];
            if (
              !sc ||
              !lineHasUserData({
                quantity: sc.quantity,
                quantity2: sc.quantity2,
                notes: sc.notes
              })
            )
              return;
            if (lineHasUserData(mergedCounts[itemId])) return;
            mergedCounts[itemId] = {
              quantity: sc.quantity ?? '',
              quantity2: sc.quantity2 ?? '',
              notes: sc.notes ?? ''
            };
          });
          if (!metaPn && parsed.personName) metaPn = parsed.personName;
          if (!metaGn && parsed.generalNotes) metaGn = parsed.generalNotes;
        }
      } catch (e) {
        console.error('Error restoring from localStorage fallback:', e);
      }
      if (metaPn) setPersonName(metaPn);
      if (metaGn) setGeneralNotes(metaGn);
      const hasLine = invList.some((it) => lineHasUserData(mergedCounts[it.id]));
      if (hasLine || metaPn || metaGn) {
        isRestoringFromStorageRef.current = true;
        setCounts(mergedCounts);
        logger.log('Restored from offline persistence (IndexedDB / localStorage)');
        setTimeout(() => {
          isRestoringFromStorageRef.current = false;
        }, 800);
      }
    };

    try {
      logger.log('fetchInventoryForSite called for siteId:', siteId, 'isSiteLogin:', isSiteLogin);
      const inventoryEndpoint = '/api/tenant/site/inventory';
      const recalledEndpoint = '/api/tenant/site/counts/recalled';
      
      logger.log('Fetching from endpoints:', { inventoryEndpoint, recalledEndpoint });
      
      const [inventoryRes, recalledRes] = await Promise.all([
        axiosInstance.get(inventoryEndpoint),
        axiosInstance.get(recalledEndpoint).catch((err) => {
          logger.log('Recalled endpoint error (might be expected):', err.response?.data || err.message);
          return { data: { counts: {}, batchId: null, personName: null, status: null, inventory: [] } };
        })
      ]);

      logger.log('Inventory response:', inventoryRes.data);
      logger.log('Recalled response:', recalledRes.data);

      const items = inventoryRes.data.items || [];
      setInventory(items);

      // Initialize counts object with all items set to empty
      const initialCounts = {};
      items.forEach(item => {
        initialCounts[item.id] = { quantity: '', quantity2: '', notes: '' };
      });

      // Check for recalled/rejected counts first
      const hasRecalledCounts = recalledRes.data.counts && Object.keys(recalledRes.data.counts).length > 0;
      
      // Reset draft loaded flag when site changes
      draftLoadedRef.current = false;
      
        // If we have recalled counts, use them (don't restore from localStorage or server draft)
        if (hasRecalledCounts) {
          draftLoadedRef.current = true; // Mark as loaded so we don't try to load draft
        logger.log('Found recalled/rejected counts:', recalledRes.data);
        logger.log('Counts object keys:', Object.keys(recalledRes.data.counts));
        setRecalledBatchId(recalledRes.data.batchId); // Store the batchId to delete old counts on resubmit
        setEditingStatus(recalledRes.data.status); // Store status (recalled or rejected)
        
        // The endpoint returns counts object keyed by itemId
        let filledCount = 0;
        Object.keys(recalledRes.data.counts).forEach(itemId => {
          if (initialCounts[itemId]) {
            const countData = recalledRes.data.counts[itemId];
            // Preserve exact decimals as entered (no rounding) when repopulating recalled counts
            initialCounts[itemId] = {
              quantity: countData.quantity != null && countData.quantity !== '' ? String(countData.quantity) : '',
              quantity2: countData.quantity2 != null && countData.quantity2 !== '' ? String(countData.quantity2) : '',
              notes: countData.notes || ''
            };
            filledCount++;
          }
        });
        logger.log(`Pre-filled ${filledCount} items with recalled/rejected data`);
        
        // Recalled data is authoritative within this period, including blank metadata.
        setPersonName(recalledRes.data.personName || '');
        setGeneralNotes(recalledRes.data.generalNotes || '');
      } else {
        logger.log('No recalled/rejected counts found. Response data:', recalledRes.data);
        setRecalledBatchId(null); // No recalled/rejected counts for this site
        setEditingStatus(null);
        // Start from a clean form before same-period draft hydration. This prevents
        // metadata already in component state from surviving a period/site reload.
        setPersonName(isSiteLogin ? '' : (user?.name || ''));
        setGeneralNotes('');
        
        // For both site and manager/team: try server draft first, then localStorage fallback.
        isRestoringFromStorageRef.current = false;
      }

      // Set counts (either from recalled data or empty initial counts)
      if (!isRestoringFromStorageRef.current || hasRecalledCounts) {
        setCounts(initialCounts);
        logger.log('Set initial counts state (empty or recalled):', initialCounts);
        // Load draft from server first, then localStorage fallback (for both site and manager/team)
        if (!hasRecalledCounts && siteId) {

          setTimeout(() => {
            loadDraftFromServer(siteId, items, tryOfflinePersistenceFallback);
          }, 500);
        }
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      if (isSiteLogin && initialInventory && initialInventory.length > 0) {
        const fallbackInv = initialInventory;
        setInventory(fallbackInv);
        const initialCounts = {};
        fallbackInv.forEach((item) => {
          initialCounts[item.id] = { quantity: '', quantity2: '', notes: '' };
        });
        setCounts(initialCounts);
        draftLoadedRef.current = false;
        isRestoringFromStorageRef.current = false;
        setTimeout(() => {
          void loadDraftFromServer(siteId, fallbackInv, tryOfflinePersistenceFallback);
        }, 100);
      } else {
        setInventory([]);
        setCounts({});
      }
    } finally {
      setRecallStateResolved(true);
    }
  };

  // Parse to number for submission (no rounding - save exact decimals)
  const parseDecimal = (val) => {
    if (val === '' || val === null || val === undefined) return null;
    const n = parseFloat(String(val).replace(',', '.'));
    return isNaN(n) ? null : n;
  };

  // Display quantity to 2 decimal places (e.g. 12.5 -> "12.50")
  const formatQuantityTo2Display = (val) => {
    if (val === null || val === undefined || val === '') return '-';
    const n = parseFloat(String(val).replace(',', '.'));
    return isNaN(n) ? '-' : Number(n).toFixed(2);
  };

  /** Review/submit line has a strictly positive count on UOM1 or UOM2 (same rule as notes / entered lines). */
  const countEntryHasPositiveQty = (c) => {
    if (!c) return false;
    const a = parseFloat(c.countedQuantity);
    const b = parseFloat(c.countedQuantity2);
    return (Number.isFinite(a) && a > 0) || (Number.isFinite(b) && b > 0);
  };

  // Sanitize decimal input: allow digits and one decimal point, max 2 decimal places
  const sanitizeDecimalInput = (value) => {
    if (value === '') return '';
    const withDots = value.replace(',', '.').replace(/[^0-9.]/g, '');
    const parts = withDots.split('.');
    if (parts.length > 2) return parts[0] + '.' + parts.slice(1).join('').substring(0, 2);
    if (parts.length === 2 && parts[1].length > 2) return parts[0] + '.' + parts[1].substring(0, 2);
    return withDots;
  };

  const handleQuantityChange = (itemId, value) => {
    if (value === '') {
      setCounts(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], quantity: '' }
      }));
      return;
    }
    const sanitized = sanitizeDecimalInput(value);
    setCounts(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity: sanitized }
    }));
  };

  const handleQuantity2Change = (itemId, value) => {
    if (value === '') {
      setCounts(prev => ({
        ...prev,
        [itemId]: { ...prev[itemId], quantity2: '' }
      }));
      return;
    }
    const sanitized = sanitizeDecimalInput(value);
    setCounts(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], quantity2: sanitized }
    }));
  };

  const handleItemNotesChange = (itemId, value) => {
    setCounts(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        notes: value
      }
    }));
  };

  // Set initial site if provided (for editing recalled counts)
  useEffect(() => {
    if (initialSiteId) {
      if (!selectedSiteId) {
        setSelectedSiteId(initialSiteId);
        if (onSiteSelected) {
          onSiteSelected(initialSiteId);
        }
        prevInitialSiteIdRef.current = initialSiteId;
      } else {
        prevInitialSiteIdRef.current = initialSiteId;
      }
    }
  }, [initialSiteId, onSiteSelected, selectedSiteId]);

  // When the priority zero-count confirmation modal is shown, ensure submitting is false so the user can click Confirm or Cancel
  useEffect(() => {
    if (showPriorityConfirmation && priorityZeroItems.length > 0) {
      setSubmitting(false);
    }
  }, [showPriorityConfirmation, priorityZeroItems.length]);

  // Force reload for site logins when forceReload changes (for editing recalled counts)
  useEffect(() => {
    if (isSiteLogin && selectedSiteId && forceReload > 0 && prevForceReloadRef.current !== forceReload) {
      logger.log('Force reload triggered for site:', selectedSiteId, 'forceReload:', forceReload);
      prevForceReloadRef.current = forceReload;
      // Clear localStorage when editing recalled counts
      clearLocalStorage(selectedSiteId);
      // Reset form state before reloading
      setRecalledBatchId(null);
      setEditingStatus(null);
      setPersonName('');
      setGeneralNotes('');
      setCounts({});
      // Fetch the recalled counts - use a small delay to ensure state is reset
      setTimeout(() => {
        fetchInventoryForSite(selectedSiteId);
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceReload, isSiteLogin, selectedSiteId]);

  // Fetch counting period on mount so we can invalidate local drafts when admin starts new period
  useEffect(() => {
    if (countingPeriodFetchedRef.current || !user?.id) return;
    let cancelled = false;
    axiosInstance.get('/api/tenant/counts/counting-period')
      .then(res => {
        if (!cancelled) {
          setCountingPeriodStartedAt(res.data.periodStartedAt ?? null);
          countingPeriodFetchedRef.current = true;
          setCountingPeriodReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) { countingPeriodFetchedRef.current = true; setCountingPeriodReady(true); }
      });
    return () => { cancelled = true; };
  }, [user?.id]);

  // Browser left open: re-check counting period on focus/visibility; clear form if period changed
  useEffect(() => {
    if (!user?.id) return undefined;
    let busy = false;
    const onPeriodPossiblyChanged = async () => {
      if (busy || document.visibilityState === 'hidden') return;
      busy = true;
      try {
        const res = await axiosInstance.get('/api/tenant/counts/counting-period');
        const next = res.data.periodStartedAt ?? null;
        const prev = countingPeriodStartedAt;
        if (normalizePeriodKey(next) === normalizePeriodKey(prev) && countingPeriodFetchedRef.current) {
          return;
        }
        // Period changed (or first successful refresh after a prior null mismatch)
        if (countingPeriodFetchedRef.current && normalizePeriodKey(next) !== normalizePeriodKey(prev)) {
          logger.log('Counting period changed while page open. Clearing drafts.', { prev, next });
          try {
            const userId = user.id;
            const prefix = `countSubmission_${userId}_`;
            for (let i = localStorage.length - 1; i >= 0; i--) {
              const key = localStorage.key(i);
              if (key && key.startsWith(prefix)) localStorage.removeItem(key);
            }
          } catch (_) { /* ignore */ }
          try {
            if (offlineDraft.clearScreenStorage) await offlineDraft.clearScreenStorage();
          } catch (_) { /* ignore */ }
          draftLoadedRef.current = false;
          isRestoringFromStorageRef.current = false;
          setShowReview(false);
          setReviewData(null);
          setPersonName(isSiteLogin ? '' : (user?.name || ''));
          setGeneralNotes('');
          setRecalledBatchId(null);
          setEditingStatus(null);
          const blank = {};
          inventory.forEach((item) => {
            blank[item.id] = { quantity: '', quantity2: '', notes: '' };
          });
          setCounts(blank);
          setCountingPeriodStartedAt(next);
          countingPeriodFetchedRef.current = true;
          setCountingPeriodReady(true);
          setSuccess('A new counting period has started. Previous draft entries were cleared.');
          setTimeout(() => setSuccess(''), 8000);
        } else if (!countingPeriodFetchedRef.current) {
          setCountingPeriodStartedAt(next);
          countingPeriodFetchedRef.current = true;
          setCountingPeriodReady(true);
        }
      } catch (_) {
        /* ignore transient errors */
      } finally {
        busy = false;
      }
    };
    const onFocus = () => { void onPeriodPossiblyChanged(); };
    const onVis = () => {
      if (document.visibilityState === 'visible') void onPeriodPossiblyChanged();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, countingPeriodStartedAt, selectedSiteId, inventory, isSiteLogin]);

  // Save to localStorage whenever counts change (but not when editing recalled counts)
  useEffect(() => {
    if (countingPeriodReady && selectedSiteId && Object.keys(counts).length > 0 && !recalledBatchId && !isRestoringFromStorageRef.current) {
      // Use setTimeout to avoid saving during initial load
      const timer = setTimeout(() => {
        if (!isRestoringFromStorageRef.current) {
          saveToLocalStorage();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counts, selectedSiteId, recalledBatchId, countingPeriodStartedAt, countingPeriodReady]);

  // Save to localStorage whenever personName or generalNotes change (but not when editing recalled counts)
  useEffect(() => {
    if (countingPeriodReady && selectedSiteId && !recalledBatchId && !isRestoringFromStorageRef.current) {
      // Use setTimeout to avoid saving during initial load
      const timer = setTimeout(() => {
        if (!isRestoringFromStorageRef.current) {
          saveToLocalStorage();
        }
      }, 200);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personName, generalNotes, selectedSiteId, recalledBatchId, countingPeriodStartedAt, countingPeriodReady]);

  // Note: Draft loading from server is handled in fetchInventoryForSite after initial counts are set
  // This ensures it happens after inventory is loaded and doesn't conflict with localStorage restoration

  // Restore from localStorage when user and site are loaded (if inventory is already loaded)
  // If counting period has changed (admin started new period), do not restore and clear local draft
  useEffect(() => {
    // If user is loaded, site is selected, inventory is loaded, but counts are empty, try to restore
    const currentCountsEmpty = Object.keys(counts).length === 0;
    if (user && user.id && selectedSiteId && inventory.length > 0 && currentCountsEmpty && !recalledBatchId && !isRestoringFromStorageRef.current) {
      const userId = user.id;
      let parsed;
      try {
        parsed = loadCurrentPeriodLocalDraft(userId, selectedSiteId);
      } catch (error) {
        console.error('Error reading localStorage draft:', error);
        return;
      }

      if (!parsed) {
        return;
      }
      
      const doRestore = () => {
        isRestoringFromStorageRef.current = true;
        try {
          logger.log('Found current-period saved data in localStorage:', {
            savedSiteId: parsed.selectedSiteId,
            currentSiteId: selectedSiteId
          });
          
          if (parsed.selectedSiteId !== selectedSiteId) {
            isRestoringFromStorageRef.current = false;
            return;
          }
          
          const mergedCounts = {};
          inventory.forEach(item => {
            mergedCounts[item.id] = { quantity: '', quantity2: '', notes: '' };
          });
          let hasRestoredData = false;
          if (parsed.counts && Object.keys(parsed.counts).length > 0) {
            Object.keys(parsed.counts).forEach(itemId => {
              if (mergedCounts[itemId]) {
                const savedCount = parsed.counts[itemId];
                if (savedCount && ((savedCount.quantity !== '' && savedCount.quantity !== undefined) || (savedCount.quantity2 !== '' && savedCount.quantity2 !== undefined) || (savedCount.notes && savedCount.notes.trim() !== ''))) {
                  mergedCounts[itemId] = { quantity: savedCount.quantity ?? '', quantity2: savedCount.quantity2 ?? '', notes: savedCount.notes ?? '' };
                  hasRestoredData = true;
                }
              }
            });
          }
          
          if (hasRestoredData || parsed.personName || parsed.generalNotes) {
            setPersonName(parsed.personName || '');
            setGeneralNotes(parsed.generalNotes || '');
            setCounts(mergedCounts);
            logger.log('Successfully restored counts from localStorage. Item count:', Object.keys(mergedCounts).length);
            setTimeout(() => { isRestoringFromStorageRef.current = false; }, 1500);
          } else {
            isRestoringFromStorageRef.current = false;
          }
        } catch (error) {
          console.error('Error restoring from localStorage:', error);
          isRestoringFromStorageRef.current = false;
        }
      };
      
      doRestore();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedSiteId, inventory, countingPeriodStartedAt]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get('/api/tenant/settings/global-inventory-value-limit');
        if (cancelled) return;
        setGlobalInventoryValueLimit(res.data?.globalInventoryValueLimit ?? null);
        setGlobalInventoryValueLimitsByCluster(res.data?.globalInventoryValueLimitsByCluster ?? {});
      } catch (_) {
        if (!cancelled) {
          setGlobalInventoryValueLimit(null);
          setGlobalInventoryValueLimitsByCluster({});
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sync inventory from props when it changes (for site logins)
  useEffect(() => {
    if (isSiteLogin && initialInventory && initialInventory.length > 0) {
      setInventory(initialInventory);
    }
  }, [initialInventory, isSiteLogin]);

  // Fetch only after the server period is known. Draft/local metadata must never
  // hydrate against the initial null period while the period request is in flight.
  useEffect(() => {
    if (!countingPeriodReady) return;
    if (selectedSiteId) {
      // Only fetch if forceReload is 0 (initial load) or if it's not a site login
      // For site logins with forceReload > 0, the forceReload effect will handle it
      if (!isSiteLogin || forceReload === 0) {
        fetchInventoryForSite(selectedSiteId);
      }
    } else {
      setInventory(initialInventory || []);
      setCounts({});
      setRecalledBatchId(null);
      setEditingStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteId, countingPeriodReady, countingPeriodStartedAt]);
  useEffect(() => {
    if (!showReview || !reviewData) return;
    const noteRequired = (c) => submissionNoteRequiredFromFlags(c.flagTypes);
    const stillMissing = reviewData.counts.filter(
      (c) => noteRequired(c) && (!c.itemNotes || String(c.itemNotes).trim() === '')
    );
    if (stillMissing.length === 0) setMissingCommentModal(null);
  }, [showReview, reviewData]);

  useLayoutEffect(() => {
    if (!showReview || !reviewData) return undefined;
    const el = reviewPanelRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = el.getBoundingClientRect().width;
      setReviewUseCardLayout(w <= 767);
    };
    update();
    window.addEventListener('resize', update);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    }
    return () => {
      window.removeEventListener('resize', update);
      if (ro) ro.disconnect();
    };
  }, [showReview, reviewData]);

  /** Scroll to and focus the Notes field for a review row (shared by missing-comment modal primary action; same logic as previous submit-block path). */
  const scrollFocusFirstMissingNote = (inventoryItemId) => {
    if (inventoryItemId == null || inventoryItemId === '') return;
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const inputEl = reviewNotesInputRefs.current[inventoryItemId];
        const rowEl = document.getElementById('cs-review-row-' + inventoryItemId);
        const scrollTarget = inputEl || rowEl;
        if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
          scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        if (inputEl) {
          try {
            inputEl.focus({ preventScroll: true });
          } catch (_) {
            try {
              inputEl.focus();
            } catch (_2) {
              /* ignore */
            }
          }
        }
      });
    });
  };

  useEffect(() => {
    if (!missingCommentModal) return;
    const t = window.setTimeout(() => {
      try {
        missingCommentModalPrimaryRef.current?.focus();
      } catch (_) {
        /* ignore */
      }
    }, 0);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setMissingCommentModal(null);
        window.setTimeout(() => {
          try {
            submitToManagerBtnRef.current?.focus();
          } catch (_) {
            /* ignore */
          }
        }, 0);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [missingCommentModal]);

  const closeConnectionRequiredModal = useCallback(() => {
    setConnectionRequiredModalOpen(false);
    window.setTimeout(() => {
      try {
        if (connectionModalReturnFocusRef.current === 'submit') {
          submitToManagerBtnRef.current?.focus();
        } else {
          reviewCountBtnRef.current?.focus();
        }
      } catch (_) {
        /* ignore */
      }
    }, 0);
  }, []);

  const openConnectionRequiredModal = useCallback((returnFocus) => {
    connectionModalReturnFocusRef.current = returnFocus;
    setConnectionRequiredModalOpen(true);
  }, []);

  useEffect(() => {
    if (!connectionRequiredModalOpen) return;
    const t = window.setTimeout(() => {
      try {
        connectionModalOkRef.current?.focus();
      } catch (_) {
        /* ignore */
      }
    }, 0);
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeConnectionRequiredModal();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [connectionRequiredModalOpen, closeConnectionRequiredModal]);

  const renderPendingFinalSubmissionBanner = () => {
    if (!pendingFinalBannerVisible || !pendingFinalSubmissionEligible) return null;
    return (
      <div className="cs-pending-final-banner" role="status" aria-live="polite">
        <div className="cs-pending-final-banner-text">
          <strong className="cs-sync-status-headline">
            {PENDING_FINAL_SUBMISSION_COPY.bannerHeadline}
          </strong>
          <p className="cs-sync-status-detail">{PENDING_FINAL_SUBMISSION_COPY.bannerDetail}</p>
        </div>
        <button
          type="button"
          className="btn btn-sm btn-primary cs-pending-final-banner-btn"
          onClick={handleCheckPendingFinalProgress}
        >
          {PENDING_FINAL_SUBMISSION_COPY.checkProgressLabel}
        </button>
      </div>
    );
  };

  const renderPendingFinalSubmissionModal = () => (
    <PendingFinalSubmissionModal
      open={pendingFinalModalOpen}
      onStayOnPage={dismissPendingFinalSubmissionModal}
      onCheckProgress={handleCheckPendingFinalProgress}
    />
  );

  const renderFinalSubmissionSuccessModal = () => (
    <FinalSubmissionSuccessModal
      open={finalSubmissionSuccessModalOpen}
      onConfirm={handleFinalSubmissionSuccessConfirm}
    />
  );

  const renderConnectionRequiredModal = () => {
    if (!connectionRequiredModalOpen) return null;
    return (
      <div
        className="cs-modal-overlay"
        onClick={closeConnectionRequiredModal}
        role="presentation"
      >
        <div
          className="cs-modal cs-missing-comment-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cs-connection-required-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h2 id="cs-connection-required-title" className="cs-modal-title cs-modal-title-neutral">
            Submission pending
          </h2>
          <p className="cs-modal-text">
            Your count has been saved on this device and will be sent automatically when the connection returns.
          </p>
          <div className="cs-modal-actions cs-missing-comment-modal-actions">
            <button
              ref={connectionModalOkRef}
              type="button"
              className="btn btn-primary"
              onClick={closeConnectionRequiredModal}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderErrorBanner = () => {
    if (!error) return null;
    const runAction = () => {
      if (!errorAction) return;
      if (errorAction.kind === 'refresh') {
        window.location.reload();
        return;
      }
      if (errorAction.kind === 'my-counts' && typeof onOpenMyCounts === 'function') {
        onOpenMyCounts();
        return;
      }
      if (errorAction.kind === 'retry') {
        setError('');
        setErrorAction(null);
      }
    };
    return (
      <div className="error-message" role="alert">
        <div>{error}</div>
        {errorAction && errorAction.label && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={runAction}>
              {errorAction.label}
            </button>
          </div>
        )}
      </div>
    );
  };

  const handleSort = (col) => {
    if (sortColumn === col) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('asc');
    }
  };

  const LOCAL_DRAFT_SAVE_FAIL_MSG =
    'We could not save your latest count on this device. Please try again before reviewing.';

  /**
   * Shared draft persistence for Save Draft and Review Count.
   * Local IndexedDB save is required when offline drafts are enabled.
   * Server sync/upload is best-effort; local success is enough to proceed to review.
   */
  const persistCurrentCountDraft = async ({ reason }) => {
    if (!selectedSiteId) {
      return { ok: false, error: 'Please select a site first' };
    }
    if (!countingPeriodReady) {
      return { ok: false, error: 'Still loading counting period. Please try again in a moment.' };
    }

    const isOnline = typeof navigator !== 'undefined' && navigator.onLine;
    const canSaveOffline =
      offlineDraft.enabled && typeof offlineDraft.persistCurrentCountDraft === 'function';

    // Prefer local persistence first so weak/offline connections cannot lose the latest entries.
    if (canSaveOffline) {
      const awaitSync = reason === 'save-draft';
      const localResult = await offlineDraft.persistCurrentCountDraft({ reason, awaitSync });
      if (!localResult || !localResult.localPersisted) {
        if (localResult && localResult.skipped && isOnline) {
          // Offline path unavailable (e.g. mid-restore) — fall through to server below.
        } else {
          return { ok: false, error: LOCAL_DRAFT_SAVE_FAIL_MSG, mode: 'local-failed' };
        }
      } else if (!isOnline || reason === 'review-count') {
        // Local draft is durable. For review-count, kick a best-effort server save without blocking
        // navigation (weak networks must not freeze Preparing review...). Sync queue already started.
        if (reason === 'review-count' && isOnline) {
          void axiosInstance
            .post('/api/tenant/site/draft-counts', {
              counts,
              personName,
              generalNotes,
              currentPeriodKey: normalizePeriodKey(countingPeriodStartedAt),
              countingPeriodStartedAt
            })
            .then(() => {
              if (typeof offlineDraft.afterBulkDraftSavedOnline === 'function') {
                return offlineDraft.afterBulkDraftSavedOnline();
              }
              return null;
            })
            .catch((error) => {
              logger.warn(
                'review-count server draft save failed; local draft retained',
                error && error.message
              );
            });
        }
        return { ok: true, mode: isOnline ? 'online' : 'offline' };
      }
    }

    // Online server bulk save (Save Draft primary path, or offline-drafts disabled).
    if (isOnline) {
      try {
        await axiosInstance.post('/api/tenant/site/draft-counts', {
          counts,
          personName,
          generalNotes,
          currentPeriodKey: normalizePeriodKey(countingPeriodStartedAt),
          countingPeriodStartedAt
        });
        if (canSaveOffline && typeof offlineDraft.afterBulkDraftSavedOnline === 'function') {
          try {
            await offlineDraft.afterBulkDraftSavedOnline();
          } catch (e) {
            logger.warn('afterBulkDraftSavedOnline / flush after server draft save', e);
          }
        } else if (canSaveOffline && typeof offlineDraft.flushOfflineDraftsNow === 'function') {
          try {
            await offlineDraft.flushOfflineDraftsNow();
          } catch (e) {
            logger.warn('flushOfflineDraftsNow after server draft save', e);
          }
        }
        return { ok: true, mode: 'online' };
      } catch (error) {
        if (error.response && error.response.status === 409) {
          return {
            ok: false,
            error:
              'A new counting period has started. Previous draft entries were cleared. Refresh and start counting for the new period.',
            serverError: error,
            conflict: true
          };
        }
        if (canSaveOffline) {
          // Local already persisted above for save-draft when online; ensure flush if we skipped.
          try {
            if (typeof offlineDraft.persistCurrentCountDraft === 'function') {
              const fallback = await offlineDraft.persistCurrentCountDraft({ reason, awaitSync: true });
              if (fallback && fallback.localPersisted) {
                return { ok: true, mode: 'offline', serverError: error };
              }
            }
            if (typeof offlineDraft.flushOfflineDraftsNow === 'function') {
              await offlineDraft.flushOfflineDraftsNow();
              return { ok: true, mode: 'offline', serverError: error };
            }
          } catch (e) {
            console.error('Offline draft flush after save failure:', e);
          }
        }
        const errorMessage =
          (error.response && error.response.data && error.response.data.error) || 'Failed to save draft';
        return { ok: false, error: errorMessage, serverError: error };
      }
    }

    // Offline and no local path
    if (canSaveOffline) {
      try {
        const localResult = await offlineDraft.persistCurrentCountDraft({ reason, awaitSync: true });
        if (localResult && localResult.localPersisted) {
          return { ok: true, mode: 'offline' };
        }
      } catch (e) {
        console.error('Offline draft save failed:', e);
      }
      return { ok: false, error: LOCAL_DRAFT_SAVE_FAIL_MSG, mode: 'local-failed' };
    }

    return { ok: false, error: LOCAL_DRAFT_SAVE_FAIL_MSG, mode: 'local-failed' };
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (preparingReview || savingDraft) return;
    setError('');
    setSuccess('');
    setMissingCommentModal(null);

    if (!isSiteLogin && !selectedSiteId) {
      setError('Please select a site');
      return;
    }

    if (!personName || personName.trim() === '') {
      setError('Person Counting name is required');
      return;
    }

    // Include items with at least one quantity entered, OR priority items (so zero-count priority rows appear in review)
    const isPriorityItem = (item) => item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1';
    const countsToSubmit = inventory
      .filter(item => {
        const countData = counts[item.id];
        const hasQuantity = countData && (
          (countData.quantity !== '' && countData.quantity !== null && countData.quantity !== undefined) ||
          (countData.quantity2 !== '' && countData.quantity2 !== null && countData.quantity2 !== undefined)
        );
        return hasQuantity || isPriorityItem(item);
      })
      .map(item => {
        const countData = counts[item.id] || {};
        return {
          inventoryItemId: item.id,
          countedQuantity: parseDecimal(countData.quantity) ?? 0,
          countedQuantity2: parseDecimal(countData.quantity2) ?? 0,
          itemNotes: (countData.notes || '')
        };
      });

    if (countsToSubmit.length === 0) {
      setError('Please enter at least one count quantity');
      return;
    }
    const hasAnyNonZeroCount = countsToSubmit.some(c => {
      const a = parseFloat(c.countedQuantity);
      const b = parseFloat(c.countedQuantity2);
      return (Number.isFinite(a) && a > 0) || (Number.isFinite(b) && b > 0);
    });
    if (!hasAnyNonZeroCount) {
      setError('Please enter at least one count quantity before reviewing.');
      return;
    }

    // Persist latest on-screen values (items, person, notes) before opening review.
    setPreparingReview(true);
    try {
      const persistResult = await persistCurrentCountDraft({ reason: 'review-count' });
      if (!persistResult.ok) {
        setError(persistResult.error || LOCAL_DRAFT_SAVE_FAIL_MSG);
        setTimeout(() => setError(''), 8000);
        return;
      }
    } catch (err) {
      console.error('Review Count auto-save failed:', err);
      setError(LOCAL_DRAFT_SAVE_FAIL_MSG);
      setTimeout(() => setError(''), 8000);
      return;
    } finally {
      setPreparingReview(false);
    }

    // Offline / unsynced drafts no longer block Review — final submit can queue locally.

    // Get site name for review
    const selectedSite = sites.find(s => s.id === selectedSiteId);
    
    const thresholdContext = buildThresholdContextForSite(
      selectedSite,
      globalInventoryValueLimit,
      globalInventoryValueLimitsByCluster
    );
    const reviewCounts = countsToSubmit.map(count => {
      const item = inventory.find(i => i.id === count.inventoryItemId);
      const isPriority = item ? (item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1') : false;
      const { flagTypes } = computeReviewFlags({
        inventoryItem: item || {},
        wasCounted: true,
        countedQuantity: count.countedQuantity,
        countedQuantity2: count.countedQuantity2,
        thresholdContext
      });
      return {
        ...count,
        itemName: item ? friendlyItemLabel(item) : 'Unknown',
        pivotCategory: item ? (item.pivotCategory) : '',
        packSize: item ? (item.packSize || '') : '',
        unit1: item ? (item.unit1) : '',
        unit2: item ? (item.unit2 || '') : '',
        expectedQuantity: item ? item.expectedQuantity : 0,
        location: item ? (item.location || '') : '',
        priority: isPriority,
        flagTypes,
        isHighValue: flagTypes.includes(FLAG_HIGH_VALUE),
        isGlobalValueThreshold: flagTypes.includes(FLAG_GLOBAL_VALUE_THRESHOLD)
      };
    });

    setReviewData({
      siteId: selectedSiteId,
      siteName: selectedSite ? selectedSite.name : 'Unknown',
      personName: personName.trim(),
      generalNotes: generalNotes.trim(),
      counts: reviewCounts
    });

    // Debug: Log review data to check priority values
    logger.log('Review data set with counts:', reviewCounts.map(c => ({
      itemName: c.itemName,
      priority: c.priority,
      countedQuantity: c.countedQuantity,
      priorityType: typeof c.priority
    })));

    setShowReview(true);
  };

  const handleBackToEdit = () => {
    setShowReview(false);
    setReviewData(null);
    setMissingCommentModal(null);
  };

  // Save draft to server (and/or offline queue when enabled) — shares persistCurrentCountDraft with Review Count
  const handleSaveDraft = async () => {
    if (!selectedSiteId) {
      setError('Please select a site first');
      return;
    }
    if (savingDraft || preparingReview) return;

    setSavingDraft(true);
    setError('');
    setSuccess('');

    const clearSuccessLater = () => setTimeout(() => setSuccess(''), 7000);

    try {
      const result = await persistCurrentCountDraft({ reason: 'save-draft' });
      if (!result.ok) {
        setError(result.error || 'Failed to save draft');
        setTimeout(() => setError(''), result.conflict ? 8000 : 5000);
        return;
      }
      if (result.mode === 'online') {
        setSuccess('Draft saved. Saved online — your progress is stored on the server.');
      } else {
        setSuccess(
          'Draft saved. Saved offline on this device — it will sync to the server when you are back online.'
        );
      }
      clearSuccessLater();
    } catch (error) {
      console.error('Save draft error:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.error) || 'Failed to save draft';
      setError(errorMessage);
      setTimeout(() => setError(''), 5000);
    } finally {
      setSavingDraft(false);
    }
  };

  // Load draft from server. Pass inventoryList when calling from fetch (avoids stale closure).
  // onNoDraft: optional callback when no server draft found (e.g. try localStorage for same-device fallback)
  const loadDraftFromServer = async (siteId, inventoryList, onNoDraft) => {
    if (!user?.id || draftLoadedRef.current) {
      return;
    }

    try {
      const response = await axiosInstance.get('/api/tenant/site/draft-counts');
      const draft = response.data.draft;
      
      if (draft && draft.counts) {
        // Server may still return a legacy row; reject if it predates the active period
        if (isServerDraftStaleForPeriod(draft, countingPeriodStartedAt)) {
          logger.log('Ignoring stale server draft (predates counting period)');
          if (onNoDraft) {
            await onNoDraft(siteId, inventoryList);
          }
          return;
        }
        logger.log('Found draft on server:', draft);
        
        // Use passed-in list (from fetch) to avoid stale closure; fallback to state
        const listToUse = inventoryList && inventoryList.length > 0 ? inventoryList : inventory;
        const mergedCounts = {};
        listToUse.forEach(item => {
          mergedCounts[item.id] = { quantity: '', quantity2: '', notes: '' };
        });
        
        let hasDraftData = false;
        Object.keys(draft.counts).forEach(itemId => {
          if (mergedCounts[itemId]) {
            const draftCount = draft.counts[itemId];
            mergedCounts[itemId] = {
              quantity: draftCount.quantity ?? '',
              quantity2: draftCount.quantity2 ?? '',
              notes: draftCount.notes ?? ''
            };
            if (draftCount.quantity || draftCount.quantity2 || draftCount.notes) {
              hasDraftData = true;
            }
          }
        });
        
        if (hasDraftData || draft.personName || draft.generalNotes) {
          let finalCounts = mergedCounts;
          if (offlineDraft.enabled) {
            try {
              const m = await offlineDraft.mergeWithLocal(draft, listToUse);
              if (m) {
                finalCounts = m.mergedCounts;
                setPersonName(m.personName || '');
                setGeneralNotes(m.generalNotes || '');
              } else {
                setPersonName(draft.personName || '');
                setGeneralNotes(draft.generalNotes || '');
              }
            } catch (e) {
              console.warn('mergeWithLocal', e);
              setPersonName(draft.personName || '');
              setGeneralNotes(draft.generalNotes || '');
            }
          } else {
            setPersonName(draft.personName || '');
            setGeneralNotes(draft.generalNotes || '');
          }
          setCounts(finalCounts);
          setSuccess('Draft loaded from server. You can continue where you left off.');
          setTimeout(() => setSuccess(''), 5000);
          draftLoadedRef.current = true;
        }
      } else if (onNoDraft) {
        await onNoDraft(siteId, inventoryList);
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Error loading draft from server:', error);
      }
      // 404 is expected if no draft exists - try localStorage fallback for same device
      if (onNoDraft) {
        await onNoDraft(siteId, inventoryList);
      }
    }
  };

  const handleReviewNotesChange = (inventoryItemId, value) => {
    if (!reviewData) return;
    setReviewData(prev => ({
      ...prev,
      counts: prev.counts.map(c =>
        c.inventoryItemId === inventoryItemId ? { ...c, itemNotes: value } : c
      )
    }));
  };

  const renderReviewNotesEditor = (count) => {
    const noteReq = submissionNoteRequiredFromFlags(count.flagTypes);
    const missing = noteReq && !(count.itemNotes && count.itemNotes.trim());
    const nid = count.inventoryItemId;
    const notePlaceholder = noteReq
      ? (flagTypeLabels(count.flagTypes).join('; ') || 'Note required')
      : 'Add note...';
    return (
      <div className="cs-review-note-cell">
        <input
          type="text"
          id={nid ? `cs-review-note-input-${nid}` : undefined}
          aria-invalid={missing ? true : undefined}
          aria-describedby={missing && nid ? `cs-review-note-hint-${nid}` : undefined}
          className={'cs-review-notes-input' + (missing ? ' cs-review-notes-required' : '')}
          placeholder={noteReq ? `Note required (${notePlaceholder})` : 'Add note...'}
          value={count.itemNotes || ''}
          onChange={(e) => handleReviewNotesChange(count.inventoryItemId, e.target.value)}
          title={noteReq ? 'A note is required for this item' : 'Add or edit a note for this item'}
          aria-required={noteReq}
          ref={(el) => {
            if (nid) {
              if (el) reviewNotesInputRefs.current[nid] = el;
              else delete reviewNotesInputRefs.current[nid];
            }
          }}
        />
        {missing && nid ? (
          <span id={`cs-review-note-hint-${nid}`} className="cs-review-note-inline-error">
            Comment required
          </span>
        ) : null}
      </div>
    );
  };

  const handleSubmitToManager = async () => {
    if (!reviewData) return;
    // Do not set submitting true here; we may show the priority confirmation modal and need the Confirm button enabled.
    // submitting is set true only when we actually call proceedWithSubmission().

    // Double-check person name is provided
    if (!reviewData.personName || reviewData.personName.trim() === '') {
      setMissingCommentModal(null);
      setError('Person Counting name is required');
      setSubmitting(false);
      return;
    }

    const noteRequired = (c) => submissionNoteRequiredFromFlags(c.flagTypes);
    const itemsMissingNote = reviewData.counts.filter(
      c => noteRequired(c) && (!c.itemNotes || String(c.itemNotes).trim() === '')
    );
    if (itemsMissingNote.length > 0) {
      const n = itemsMissingNote.length;
      const firstId = itemsMissingNote[0].inventoryItemId;
      setError('');
      setMissingCommentModal({
        count: n,
        firstInventoryItemId: firstId
      });
      setSubmitting(false);
      return;
    }

    // Check for zero-count OR no-entry priority items
    // We need to check ALL priority items in inventory, not just the ones submitted
    const zeroCountOrNoEntryPriorityItems = inventory
      .filter(item => {
        // Check if item is priority (handle both boolean and string values)
        const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1';
        if (!isPriority) return false;
        
        // Check if this priority item was submitted
        const submittedCount = reviewData.counts.find(c => c.inventoryItemId === item.id);
        
        if (submittedCount) {
          // Item was submitted — flag only when neither UOM has a positive count (UOM2-only counts count as entered)
          return !countEntryHasPositiveQty(submittedCount);
        } else {
          // Item was NOT submitted (no entry) - this is also a problem for priority items
          return true;
        }
      })
      .map(item => {
        const submittedCount = reviewData.counts.find(c => c.inventoryItemId === item.id);
        return {
          itemName: inventoryNotificationLabel(item) ?? 'Unknown',
          inventoryItemId: item.id,
          hasNoEntry: !submittedCount,
          countedQuantity: submittedCount ? submittedCount.countedQuantity : null,
          countedQuantity2: submittedCount ? submittedCount.countedQuantity2 : null
        };
      });

    logger.log('Zero-count or no-entry priority items found:', zeroCountOrNoEntryPriorityItems.length, zeroCountOrNoEntryPriorityItems);

    if (zeroCountOrNoEntryPriorityItems.length > 0) {
      // Show confirmation dialog so user can confirm; re-enable buttons in the modal
      setSubmitting(false);
      setPriorityZeroItems(zeroCountOrNoEntryPriorityItems);
      setShowPriorityConfirmation(true);
      return; // Don't proceed with submission yet
    }

    // Proceed with submission
    await proceedWithSubmission();
  };

  const proceedWithSubmission = async () => {
    if (!reviewData) return;

    setSubmitting(true);
    setError('');
    setSuccess('');
    setMissingCommentModal(null);

    try {
      if (offlineDraft.flushOfflineDraftsNow) {
        try {
          await offlineDraft.flushOfflineDraftsNow();
        } catch (e) {
          console.warn('flushOfflineDraftsNow', e);
        }
      }

      // Filter out counts with empty quantities (both quantity and quantity2)
      const countsToSubmit = reviewData.counts.filter(c => 
        (c.countedQuantity !== '' && c.countedQuantity !== null && c.countedQuantity !== undefined) ||
        (c.countedQuantity2 !== '' && c.countedQuantity2 !== null && c.countedQuantity2 !== undefined)
      );

      if (countsToSubmit.length === 0) {
        setError('Please enter at least one count quantity before submitting.');
        setSubmitting(false);
        return;
      }

      const mappedCounts = countsToSubmit.map(c => ({
        itemId: c.inventoryItemId,
        quantity: c.countedQuantity,
        quantity2: c.countedQuantity2 || 0,
        notes: c.itemNotes || ''
      }));

      // Preferred path: durable local pending final submission + background retry
      if (finalSubmission.enabled && typeof finalSubmission.submitFinalToManager === 'function') {
        const result = await finalSubmission.submitFinalToManager({
          personName: reviewData.personName,
          generalNotes: reviewData.generalNotes,
          recalledBatchId: recalledBatchId || null,
          counts: mappedCounts
        });

        if (result.outcome === 'submitted') {
          const currentSiteId = selectedSiteId;
          clearLocalStorage(currentSiteId);
          try {
            if (offlineDraft.clearScreenStorage) await offlineDraft.clearScreenStorage();
          } catch (e) {
            console.warn('clearScreenStorage', e);
          }
          try {
            await axiosInstance.delete('/api/tenant/site/draft-counts');
          } catch (error) {
            console.warn('Could not delete draft from server:', error);
          }
          setRecalledBatchId(null);
          setEditingStatus(null);
          setShowPriorityConfirmation(false);
          setPriorityZeroItems([]);
          showFinalSubmissionSuccessModal();
          return;
        }

        if (result.outcome === 'pending') {
          setError('');
          setErrorAction(null);
          setSuccess(
            'Submission pending. Your count has been saved on this device and will be sent automatically when the connection returns.'
          );
          setShowPriorityConfirmation(false);
          setPriorityZeroItems([]);
          const pendingRow = (result && result.row) || finalSubmission.pendingRow || null;
          const pendingKey = pendingFinalSubmissionKey(pendingRow) || 'pending';
          setConfirmedPendingSubmissionKey(pendingKey);
          openPendingFinalSubmissionModal(pendingRow || pendingKey);
          // Keep review visible with pending status; do not reload or clear until server confirms.
          return;
        }

        if (result.outcome === 'stale') {
          setSuccess('');
          const friendly = getFriendlyError(
            {
              response: {
                status: 409,
                data: {
                  code: 'STALE_COUNTING_PERIOD',
                  userMessage: result.message,
                  error: result.message
                }
              }
            },
            { kind: 'final_submit' }
          );
          setError(friendly.message);
          setErrorAction({ label: friendly.actionLabel || 'Refresh', kind: 'refresh' });
          return;
        }

        setSuccess('');
        {
          const friendly = getFriendlyError(
            {
              response: {
                status: 400,
                data: {
                  code:
                    result.message && /active count awaiting manager review|ACTIVE_BATCH/i.test(result.message)
                      ? 'ACTIVE_BATCH_EXISTS'
                      : 'SUBMISSION_FAILED',
                  error: result.message,
                  userMessage: result.message
                }
              }
            },
            { kind: 'final_submit_failed' }
          );
          setError(friendly.message || result.message || 'Failed to submit counts');
          if (friendly.code === 'ACTIVE_BATCH_EXISTS') {
            setErrorAction({ label: friendly.actionLabel || 'Open My Counts', kind: 'my-counts' });
          } else {
            setErrorAction({ label: friendly.actionLabel || 'Try again', kind: 'retry' });
          }
        }
        return;
      }

      // Fallback when offline final-submission queue is unavailable: online-only submit
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        openConnectionRequiredModal('submit');
        return;
      }

      const endpoint = '/api/tenant/site/counts';
      const requestBody = {
        personName: reviewData.personName,
        generalNotes: reviewData.generalNotes,
        recalledBatchId: recalledBatchId || null,
        currentPeriodKey: normalizePeriodKey(countingPeriodStartedAt),
        countingPeriodStartedAt,
        counts: mappedCounts
      };

      await axiosInstance.post(endpoint, requestBody);
      
      const currentSiteId = selectedSiteId;
      clearLocalStorage(currentSiteId);
      try {
        if (offlineDraft.clearScreenStorage) await offlineDraft.clearScreenStorage();
      } catch (e) {
        console.warn("clearScreenStorage", e);
      }
      
      try {
        await axiosInstance.delete('/api/tenant/site/draft-counts');
        logger.log('Deleted site draft from server after submission');
      } catch (error) {
        console.warn('Could not delete draft from server:', error);
      }
      
      setRecalledBatchId(null);
      setEditingStatus(null);
      setShowPriorityConfirmation(false);
      setPriorityZeroItems([]);
      showFinalSubmissionSuccessModal();
    } catch (error) {
      console.error('Submit error:', error);
      // Network failure with queue unavailable — still show connection modal as last resort
      if (!error.response) {
        openConnectionRequiredModal('submit');
      } else {
        const friendly = getFriendlyError(error, { kind: 'final_submit_failed' });
        setError(friendly.message);
        if (friendly.code === 'ACTIVE_BATCH_EXISTS') {
          setErrorAction({ label: friendly.actionLabel || 'Open My Counts', kind: 'my-counts' });
        } else if (friendly.code === 'STALE_COUNTING_PERIOD') {
          setErrorAction({ label: friendly.actionLabel || 'Refresh', kind: 'refresh' });
        } else {
          setErrorAction({ label: friendly.actionLabel || 'Try again', kind: 'retry' });
        }
        setTimeout(() => {
          setError('');
          setErrorAction(null);
        }, 10000);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const hasEnteredCounts = Object.values(counts).some(count =>
    count &&
    (
      (count.quantity !== '' && count.quantity !== null && count.quantity !== undefined) ||
      (count.quantity2 !== '' && count.quantity2 !== null && count.quantity2 !== undefined)
    )
  );

  const enteredCountLines = useMemo(
    () =>
      Object.values(counts).filter(c =>
        c &&
        (
          (c.quantity !== '' && c.quantity !== null && c.quantity !== undefined) ||
          (c.quantity2 !== '' && c.quantity2 !== null && c.quantity2 !== undefined)
        )
      ).length,
    [counts]
  );

  const filteredSortedInventory = useMemo(() => {
    const q = searchTerm.trim() ? searchTerm.toLowerCase().trim() : '';
    const filtered = inventory.filter((item) => {
      if (locationFilter) {
        const itemLocation = item.location || '';
        if (!itemLocation.toLowerCase().includes(locationFilter.toLowerCase())) return false;
      }
      if (q) {
        const name = ((item.name) || '').toLowerCase();
        const description = ((item.itemDescription) || '').toLowerCase();
        const category = ((item.pivotCategory) || '').toLowerCase();
        const vendor = (item.vendor || '').toLowerCase();
        const location = (item.location || '').toLowerCase();
        const vendorItemCode = (item.vendorItemCode || '').toLowerCase();
        const pivotCategory = (item.pivotCategory || '').toLowerCase();
        const unit = ((item.unit1) || '').toLowerCase();
        const packSize = (item.packSize || '').toLowerCase();
        const matchesSearch = name.includes(q) || description.includes(q) || category.includes(q) || vendor.includes(q) || location.includes(q) || vendorItemCode.includes(q) || pivotCategory.includes(q) || unit.includes(q) || packSize.includes(q);
        if (!matchesSearch) return false;
      }
      return true;
    });
    if (!sortColumn) {
      if (!q) return filtered;
      return [...filtered].sort((a, b) => {
        const ra = inventoryTextSearchScore(a, q);
        const rb = inventoryTextSearchScore(b, q);
        if (rb !== ra) return rb - ra;
        return compareItemsByFriendlyDisplay(a, b);
      });
    }
    return [...filtered].sort((a, b) => {
      let valA; let valB;
      const countA = counts[a.id] || { quantity: '', quantity2: '', notes: '' };
      const countB = counts[b.id] || { quantity: '', quantity2: '', notes: '' };
      switch (sortColumn) {
        case 'name': {
          const cmp = compareItemsByFriendlyDisplay(a, b);
          if (cmp < 0) return sortDirection === 'asc' ? -1 : 1;
          if (cmp > 0) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        }
        case 'category': valA = (a.pivotCategory || '').toLowerCase(); valB = (b.pivotCategory || '').toLowerCase(); break;
        case 'packSize': valA = (a.packSize || '').toLowerCase(); valB = (b.packSize || '').toLowerCase(); break;
        case 'unit': valA = (a.unit1 || '').toLowerCase(); valB = (b.unit1 || '').toLowerCase(); break;
        case 'countedQty': valA = parseFloat(countA.quantity) || 0; valB = parseFloat(countB.quantity) || 0; break;
        case 'unit2': valA = (a.unit2 || '').toLowerCase(); valB = (b.unit2 || '').toLowerCase(); break;
        case 'countedQty2': valA = parseFloat(countA.quantity2) || 0; valB = parseFloat(countB.quantity2) || 0; break;
        case 'location': valA = (a.location || '').toLowerCase(); valB = (b.location || '').toLowerCase(); break;
        case 'priority':
          valA = (a.priority === true || a.priority === 'true' || a.priority === 1 || a.priority === '1') ? 1 : 0;
          valB = (b.priority === true || b.priority === 'true' || b.priority === 1 || b.priority === '1') ? 1 : 0;
          break;
        default: return 0;
      }
      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [inventory, locationFilter, searchTerm, sortColumn, sortDirection, counts]);

  const sortedReviewCounts = useMemo(() => {
    if (!reviewData?.counts?.length) return [];
    const rows = [...reviewData.counts];
    if (!sortColumn) return rows;
    const dir = sortDirection === 'asc' ? 1 : -1;
    rows.sort((a, b) => {
      let valA; let valB;
      switch (sortColumn) {
        case 'name':
          valA = (a.itemName || '').toLowerCase();
          valB = (b.itemName || '').toLowerCase();
          break;
        case 'category':
          valA = (a.pivotCategory || '').toLowerCase();
          valB = (b.pivotCategory || '').toLowerCase();
          break;
        case 'packSize':
          valA = (a.packSize || '').toLowerCase();
          valB = (b.packSize || '').toLowerCase();
          break;
        case 'unit':
          valA = (a.unit1 || '').toLowerCase();
          valB = (b.unit1 || '').toLowerCase();
          break;
        case 'unit2':
          valA = (a.unit2 || '').toLowerCase();
          valB = (b.unit2 || '').toLowerCase();
          break;
        case 'countedQty':
          valA = parseFloat(a.countedQuantity) || 0;
          valB = parseFloat(b.countedQuantity) || 0;
          break;
        case 'countedQty2':
          valA = parseFloat(a.countedQuantity2) || 0;
          valB = parseFloat(b.countedQuantity2) || 0;
          break;
        case 'location':
          valA = (a.location || '').toLowerCase();
          valB = (b.location || '').toLowerCase();
          break;
        case 'priority':
          valA = a.priority ? 1 : 0;
          valB = b.priority ? 1 : 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return -dir;
      if (valA > valB) return dir;
      return 0;
    });
    return rows;
  }, [reviewData, sortColumn, sortDirection]);

  /* Inventory layout: screen -> scrollArea -> employeeSection + toolbar + inventoryList, bottomBar fixed */
  const useInventoryLayout = selectedSiteId && inventory.length > 0;

  useEffect(() => {
    if (!useInventoryLayout) return undefined;
    const toolbarEl = toolbarRef.current;
    if (!toolbarEl) return undefined;

    const updateStickyTop = () => {
      const measured = Math.max(0, Math.round(toolbarEl.getBoundingClientRect().height));
      setStickyHeaderTop(measured);
    };

    updateStickyTop();
    window.addEventListener('resize', updateStickyTop);
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateStickyTop);
      ro.observe(toolbarEl);
    }
    return () => {
      window.removeEventListener('resize', updateStickyTop);
      if (ro) ro.disconnect();
    };
  }, [useInventoryLayout]);

  // Show priority confirmation modal
  if (showPriorityConfirmation && priorityZeroItems.length > 0) {
    return (
      <div className="card">
        <div className="cs-modal-overlay">
          <div className="cs-modal">
            <h3 className="cs-modal-title">
              Priority Items with Zero Count or No Entry
            </h3>
            <p className="cs-modal-text">
              The following priority items have been counted as <strong>zero (0)</strong> or have <strong>no entry</strong>:
            </p>
            <ul className="cs-modal-list">
              {priorityZeroItems.map((item, index) => (
                <li key={item.inventoryItemId}>
                  {item.itemName}
                  {item.hasNoEntry && (
                    <span className="cs-modal-list-detail">(No entry)</span>
                  )}
                  {!item.hasNoEntry && (
                    <span className="cs-modal-list-detail">
                      (U1: {formatQuantityTo2Display(item.countedQuantity)} · U2: {formatQuantityTo2Display(item.countedQuantity2)})
                    </span>
                  )}
                </li>
              ))}
            </ul>
            <p className="cs-modal-confirm">
              <strong>Please confirm:</strong> Have you double-checked these items to ensure they are truly zero (0) quantity or that no entry is correct?
            </p>
            <div className="cs-modal-actions">
              <button className="cs-btn-cancel" onClick={() => { setShowPriorityConfirmation(false); setPriorityZeroItems([]); setSubmitting(false); }} disabled={submitting}>
                Cancel - Go Back to Review
              </button>
              <button className="cs-btn-confirm-danger" onClick={proceedWithSubmission} disabled={submitting}>
                {submitting ? 'Submitting...' : 'Yes, I Confirm - Submit Count'}
              </button>
            </div>
          </div>
        </div>
        {renderConnectionRequiredModal()}
        {renderPendingFinalSubmissionModal()}
        {renderFinalSubmissionSuccessModal()}
      </div>
    );
  }

  // Show review screen if review data is ready
  if (showReview && reviewData) {
    return (
      <div className="cs-review-card" ref={reviewPanelRef}>
        <h3 className="cs-review-title">Review Count Before Submission</h3>
        {renderPendingFinalSubmissionBanner()}
        {offlineDraft.syncUi && offlineDraft.syncUi.headline && (
          <CountSyncStatusStrip
            syncUi={offlineDraft.syncUi}
            showRetry={Boolean(offlineDraft.syncUi.showRetryButton && offlineDraft.retryLineSync)}
            onRetry={() => offlineDraft.retryLineSync && offlineDraft.retryLineSync()}
            retryBusy={offlineDraft.lineSyncActionBusy}
          />
        )}
        {showFinalSubmissionStatus && (
          <CountSyncStatusStrip syncUi={finalSubmission.submissionUi} className="cs-final-submit-status" />
        )}
        <p className="cs-review-sync-hint">
          {pendingFinalSubmissionEligible
            ? 'Your final count is saved on this device. It will send to your manager automatically when the connection returns — you do not need to keep retrying.'
            : 'Submit to your manager from this screen when you are ready. If the connection drops, your completed count is saved on this device and sent automatically when you are back online. Then the batch appears in My Counts.'}
        </p>
        {missingCommentModal && (
          <div
            className="cs-modal-overlay"
            onClick={() => {
              setMissingCommentModal(null);
              window.setTimeout(() => {
                try {
                  submitToManagerBtnRef.current?.focus();
                } catch (_) {
                  /* ignore */
                }
              }, 0);
            }}
            role="presentation"
          >
            <div
              className="cs-modal cs-missing-comment-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="cs-missing-comment-modal-title"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 id="cs-missing-comment-modal-title" className="cs-modal-title cs-modal-title-neutral">
                Comments required
              </h2>
              <p className="cs-modal-text">
                {missingCommentModal.count === 1
                  ? 'Comments are required on 1 highlighted line in the Notes column before you can submit to your manager.'
                  : `Comments are required on ${missingCommentModal.count} highlighted lines in the Notes column before you can submit to your manager.`}{' '}
                Please add the missing comment{missingCommentModal.count === 1 ? '' : 's'}, then try again.
              </p>
              <div className="cs-modal-actions cs-missing-comment-modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setMissingCommentModal(null);
                    window.setTimeout(() => {
                      try {
                        submitToManagerBtnRef.current?.focus();
                      } catch (_) {
                        /* ignore */
                      }
                    }, 0);
                  }}
                >
                  Close
                </button>
                <button
                  ref={missingCommentModalPrimaryRef}
                  type="button"
                  className="btn btn-primary"
                  onClick={() => {
                    const id = missingCommentModal.firstInventoryItemId;
                    setMissingCommentModal(null);
                    window.setTimeout(() => scrollFocusFirstMissingNote(id), 0);
                  }}
                >
                  Go to first missing comment
                </button>
              </div>
            </div>
          </div>
        )}
        {renderConnectionRequiredModal()}
        {renderPendingFinalSubmissionModal()}
        {renderFinalSubmissionSuccessModal()}
        {error && renderErrorBanner()}
        {success && <div className="success-message">{success}</div>}
        <div className="review-summary">
          <div className="cs-review-summary">
            <p><strong>Site:</strong> {reviewData.siteName}</p>
            <p><strong>Person Counting:</strong> {reviewData.personName}</p>
            {reviewData.generalNotes && (<p><strong>General Notes:</strong> {reviewData.generalNotes}</p>)}
          </div>
          <h4>Count Summary ({reviewData.counts.length} items)</h4>
          {reviewUseCardLayout ? (
          <div className="cs-review-mobile-cards" aria-label="Count summary cards">
            {sortedReviewCounts.map((count, index) => {
              const isZeroPriority = count.priority && !countEntryHasPositiveQty(count);
              const hasUnit2 = count.unit2 && count.unit2.trim() !== '';
              return (
                <div
                  key={count.inventoryItemId || index}
                  id={count.inventoryItemId ? `cs-review-row-${count.inventoryItemId}` : undefined}
                  className={
                    'cs-mobile-card cs-review-mobile-card' +
                    (isZeroPriority ? ' cs-review-mobile-card--zero' : '')
                  }
                >
                  <div className="cs-mobile-card-header">
                    {isSiteLogin ? (
                      <>
                        <div className="cs-mobile-card-header-main">
                          <div className="cs-mobile-card-name">{count.itemName}</div>
                          <div className="cs-mobile-card-pack">{count.packSize || ''}</div>
                          <div className="cs-mobile-card-meta">
                            <span className="cs-category-pill">{count.pivotCategory}</span>
                          </div>
                        </div>
                        <div className="cs-mobile-card-badges">
                          {count.priority ? (<span className="cs-priority-pill" title="Priority Item">Priority</span>) : null}
                          {count.isHighValue ? (
                            <span className="cs-high-value-pill" title="Total value exceeds item threshold">High Value</span>
                          ) : null}
                          {count.isGlobalValueThreshold ? (
                            <span className="cs-high-value-pill" title="Total value exceeds global threshold">Global value threshold</span>
                          ) : null}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="cs-mobile-card-header-main">
                          <div className="cs-mobile-card-name">{count.itemName}</div>
                        </div>
                        <div className="cs-mobile-card-badges">
                          <span className="cs-category-pill">{count.pivotCategory}</span>
                          {count.priority ? (<span className="cs-priority-pill" title="Priority Item">Priority</span>) : null}
                          {count.isHighValue ? (
                            <span className="cs-high-value-pill" title="Total value exceeds item threshold">High Value</span>
                          ) : null}
                          {count.isGlobalValueThreshold ? (
                            <span className="cs-high-value-pill" title="Total value exceeds global threshold">Global value threshold</span>
                          ) : null}
                        </div>
                      </>
                    )}
                  </div>
                  <div className="cs-mobile-card-row">
                    <div className="cs-mobile-card-field">
                      <label>{count.unit1 || 'Unit 1'} — counted</label>
                      <div className={'cs-review-mobile-readonly-qty' + (isZeroPriority ? ' cs-review-zero-qty' : '')}>
                        {formatQuantityTo2Display(count.countedQuantity)}
                      </div>
                    </div>
                    {hasUnit2 ? (
                      <div className="cs-mobile-card-field">
                        <label>{count.unit2} — counted 2</label>
                        <div className="cs-review-mobile-readonly-qty">{formatQuantityTo2Display(count.countedQuantity2)}</div>
                      </div>
                    ) : (
                      <div className="cs-mobile-card-field" aria-hidden="true" />
                    )}
                  </div>
                  <div className="cs-mobile-card-notes cs-review-mobile-card-notes">
                    <label>Notes</label>
                    {renderReviewNotesEditor(count)}
                  </div>
                  {count.location ? (
                    <div className="cs-mobile-card-location">Location: {count.location}</div>
                  ) : null}
                </div>
              );
            })}
          </div>
          ) : (
          <div className="cs-review-table-wrap">
          <table className="cs-review-table">
            <thead>
              <tr>
                <th>Item</th>
                <th onClick={() => handleSort('category')} title="Click to sort">Category<span className="cs-sort-indicator">{sortColumn === 'category' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                {isSiteLogin ? (
                  <th onClick={() => handleSort('packSize')} title="Click to sort">Pack Size<span className="cs-sort-indicator">{sortColumn === 'packSize' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                ) : null}
                <th onClick={() => handleSort('unit')} title="Click to sort">Unit 1<span className="cs-sort-indicator">{sortColumn === 'unit' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                <th>Counted</th>
                <th>Unit 2</th>
                <th>Counted 2</th>
                <th onClick={() => handleSort('location')} title="Click to sort">Location<span className="cs-sort-indicator">{sortColumn === 'location' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                <th onClick={() => handleSort('priority')} title="Click to sort">Priority<span className="cs-sort-indicator">{sortColumn === 'priority' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                <th>Notes</th>
                <th>Alert</th>
              </tr>
            </thead>
            <tbody>
              {sortedReviewCounts.map((count, index) => {
                const isZeroPriority = count.priority && !countEntryHasPositiveQty(count);
                return (
                  <tr
                    key={count.inventoryItemId || index}
                    id={count.inventoryItemId ? `cs-review-row-${count.inventoryItemId}` : undefined}
                    className={isZeroPriority ? 'cs-review-row-zero' : ''}
                  >
                    <td>
                      <span className="cs-item-name">{count.itemName}</span>
                    </td>
                    <td><span className="cs-category-pill">{count.pivotCategory}</span></td>
                    {isSiteLogin ? (
                      <td className="cs-pack-size-cell">{count.packSize || ''}</td>
                    ) : null}
                    <td>{count.unit1}</td>
                    <td className={isZeroPriority ? 'cs-review-zero-qty' : ''}>{formatQuantityTo2Display(count.countedQuantity)}</td>
                    <td>{count.unit2 || '-'}</td>
                    <td>{formatQuantityTo2Display(count.countedQuantity2)}</td>
                    <td>{count.location || '-'}</td>
                    <td style={{ textAlign: 'center' }}>{count.priority ? (<span className="cs-priority-pill">Priority</span>) : '-'}</td>
                    <td>
                      {renderReviewNotesEditor(count)}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {count.isHighValue ? (<span className="cs-high-value-pill" title="Total value exceeds item threshold">High Value - Please re check count</span>) : null}
                      {count.isGlobalValueThreshold ? (<span className="cs-high-value-pill" title="Total value exceeds global threshold">Global value threshold - Please re check count</span>) : null}
                      {!count.isHighValue && !count.isGlobalValueThreshold ? '-' : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          )}
          <div className="cs-review-actions">
            <button
              className="btn btn-secondary"
              onClick={handleBackToEdit}
              disabled={
                submitting ||
                pendingFinalSubmissionEligible
              }
            >
              Back to Edit
            </button>
            <button
              ref={submitToManagerBtnRef}
              type="button"
              className="btn btn-primary"
              onClick={handleSubmitToManager}
              disabled={
                submitting ||
                finalSubmission.busy ||
                pendingFinalSubmissionEligible
              }
            >
              {submitting || finalSubmission.busy
                ? 'Submitting...'
                : pendingFinalSubmissionEligible
                  ? 'Submission pending'
                  : 'Submit to Manager'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div
      className={useInventoryLayout ? 'count-submission-screen' : 'card'}
      style={useInventoryLayout ? { '--cs-sticky-header-top': `${stickyHeaderTop}px` } : undefined}
    >
      <form onSubmit={handleReview} className="count-form">
        {useInventoryLayout ? (
          <>
            <div className="scrollArea">
              <div className="inventory-scroll-content">
              <section className="employeeSection">
                {isSiteLogin && siteName && (
                  <div className="cs-live-badge">
                    <span className="cs-live-dot"></span>
                    {siteName} &middot; Live Session
                  </div>
                )}
                <h3 className="count-submission-title">Submit Inventory Count</h3>
                {renderPendingFinalSubmissionBanner()}
                {offlineDraft.syncUi && offlineDraft.syncUi.headline && (
                  <CountSyncStatusStrip
                    syncUi={offlineDraft.syncUi}
                    showRetry={Boolean(offlineDraft.syncUi.showRetryButton && offlineDraft.retryLineSync)}
                    onRetry={() => offlineDraft.retryLineSync && offlineDraft.retryLineSync()}
                    retryBusy={offlineDraft.lineSyncActionBusy}
                  />
                )}
                {showFinalSubmissionStatus && (
                  <CountSyncStatusStrip syncUi={finalSubmission.submissionUi} className="cs-final-submit-status" />
                )}
                {!isSiteLogin && (
                  <div className="form-group">
                    <label>Site *</label>
                    {sites.length === 0 ? (
                      <div className="error">No sites assigned to you. Please contact your manager.</div>
                    ) : (
                      <>
                        <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} required>
                          <option value="">Select a site</option>
                          {sites.map(site => (<option key={site.id} value={site.id}>{site.name}</option>))}
                        </select>
                        {selectedSiteId && inventory.length === 0 && (<small className="form-hint">No inventory items available for this site's brand</small>)}
                      </>
                    )}
                  </div>
                )}
                {isSiteLogin && selectedSiteId && (
                  <div className="form-group">
                    <label>Site</label>
                    <input type="text" value={sites.find(s => s.id === selectedSiteId)?.name || ''} disabled className="input-disabled" />
                  </div>
                )}
                <div className="cs-employee-middle">
                  <div className="form-group cs-field-person">
                    <label>Person Counting *</label>
                    <input type="text" value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="Enter the name of the person completing the count" maxLength={200} required className={!personName || personName.trim() === '' ? 'cs-input-error' : ''} />
                    {(!personName || personName.trim() === '') && (<small className="form-error">This field is required</small>)}
                  </div>
                  {recalledBatchId && (
                    <div className="recalled-banner-compact">
                      <strong style={{ color: editingStatus === 'rejected' ? '#dc3545' : 'var(--hana-red)' }}>
                        {editingStatus === 'rejected' ? 'Editing Rejected Count' : 'Editing Recalled Count'}
                      </strong>
                      <span className="recalled-banner-text">Make your changes and resubmit.</span>
                    </div>
                  )}
                  <div className="form-group cs-field-general-notes">
                    <label>General Notes (optional)</label>
                    <textarea
                      className="cs-general-notes-input cs-general-notes-input--desktop"
                      value={generalNotes}
                      onChange={(e) => setGeneralNotes(e.target.value)}
                      rows={1}
                      maxLength={2000}
                      placeholder="Any general notes about this count session..."
                      title="Maximum 2000 characters"
                    />
                    <input
                      type="text"
                      className="cs-general-notes-input cs-general-notes-input--tablet"
                      value={generalNotes}
                      onChange={(e) => setGeneralNotes(e.target.value)}
                      maxLength={2000}
                      placeholder="Any general notes about this count session..."
                      title="Maximum 2000 characters"
                    />
                  </div>
                </div>
              </section>

              <section className="toolbar" ref={toolbarRef}>
                <div className="toolbar-filters">
                  <div className="toolbar-filter-item">
                    <label>Search</label>
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search by description, vendor name, category..." />
                  </div>
                  <div className="toolbar-filter-item">
                    <label>Location</label>
                    <select value={locationFilter} onChange={(e) => setLocationFilter(e.target.value)}>
                      <option value="">All Locations</option>
                      {Array.from(new Set(inventory.map(item => item.location).filter(loc => loc && loc.trim() !== ''))).sort().map(location => (<option key={location} value={location}>{location}</option>))}
                    </select>
                  </div>
                </div>
              </section>
              <section className="inventoryList">
                <div className="inventory-table-scroll">
                  <table className="count-table count-table--desktop">
                    <colgroup>
                      {isSiteLogin ? (
                        <>
                          <col style={{ width: '16%' }} />
                          <col style={{ width: '9%' }} />
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '11%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '15%' }} />
                        </>
                      ) : (
                        <>
                          <col style={{ width: '17%' }} />
                          <col style={{ width: '10%' }} />
                          <col style={{ width: '7%' }} />
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '6%' }} />
                          <col style={{ width: '8%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '5%' }} />
                          <col style={{ width: '17%' }} />
                        </>
                      )}
                    </colgroup>
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('name')} title="Click to sort">Item<span className="cs-sort-indicator">{sortColumn === 'name' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('category')} title="Click to sort">Category<span className="cs-sort-indicator">{sortColumn === 'category' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        {isSiteLogin ? (
                          <th onClick={() => handleSort('packSize')} title="Click to sort">Pack Size<span className="cs-sort-indicator">{sortColumn === 'packSize' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        ) : null}
                        <th className="cs-th-align-center" onClick={() => handleSort('unit')} title="Click to sort">Unit 1<span className="cs-sort-indicator">{sortColumn === 'unit' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('countedQty')} title="Click to sort">Counted Qty<span className="cs-sort-indicator">{sortColumn === 'countedQty' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th className="cs-th-align-center" onClick={() => handleSort('unit2')} title="Click to sort">Unit2<span className="cs-sort-indicator">{sortColumn === 'unit2' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('countedQty2')} title="Click to sort">Counted Qty 2<span className="cs-sort-indicator">{sortColumn === 'countedQty2' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('location')} title="Click to sort">Location<span className="cs-sort-indicator">{sortColumn === 'location' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('priority')} title="Click to sort">Priority<span className="cs-sort-indicator">{sortColumn === 'priority' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th>Item Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                  {filteredSortedInventory.map(item => {
                      const countData = counts[item.id] || { quantity: '', quantity2: '', notes: '' };
                      const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1';
                      const hasUnit2 = item.unit2 && item.unit2.trim() !== '';
                      return (
                      <tr key={item.id}>
                        <td><span className="cs-item-name">{friendlyItemLabel(item)}</span>{item.vendorItemCode && <span className="cs-item-vendor">{item.vendorItemCode}</span>}</td>
                        <td><span className="cs-category-pill">{item.pivotCategory ?? '-'}</span></td>
                        {isSiteLogin ? (
                          <td className="cs-pack-size-cell">{item.packSize || ''}</td>
                        ) : null}
                        <td className="cs-td-align-center">{item.unit1 ?? '-'}</td>
                        <td>
                          <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity} onChange={(e) => handleQuantityChange(item.id, e.target.value)} onKeyDown={(e) => { if ([46,8,9,27,13,190,110].indexOf(e.keyCode)!==-1||(e.keyCode===65&&e.ctrlKey===true)||(e.keyCode===67&&e.ctrlKey===true)||(e.keyCode===86&&e.ctrlKey===true)||(e.keyCode===88&&e.ctrlKey===true)||(e.keyCode>=35&&e.keyCode<=39))return; if((e.keyCode>=48&&e.keyCode<=57)||(e.keyCode>=96&&e.keyCode<=105))return; e.preventDefault(); }} onPaste={(e) => { e.preventDefault(); const pastedText=(e.clipboardData||window.clipboardData).getData('text'); const sanitized=sanitizeDecimalInput(pastedText); if(sanitized!==undefined&&sanitized!==null)handleQuantityChange(item.id,sanitized); }} placeholder="0" />
                        </td>
                        <td className="cs-td-align-center">{item.unit2 || '-'}</td>
                        <td>
                          <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity2||''} onChange={(e) => handleQuantity2Change(item.id, e.target.value)} onKeyDown={(e) => { if ([46,8,9,27,13,190,110].indexOf(e.keyCode)!==-1||(e.keyCode===65&&e.ctrlKey===true)||(e.keyCode===67&&e.ctrlKey===true)||(e.keyCode===86&&e.ctrlKey===true)||(e.keyCode===88&&e.ctrlKey===true)||(e.keyCode>=35&&e.keyCode<=39))return; if((e.keyCode>=48&&e.keyCode<=57)||(e.keyCode>=96&&e.keyCode<=105))return; e.preventDefault(); }} onPaste={(e) => { e.preventDefault(); const pastedText=(e.clipboardData||window.clipboardData).getData('text'); const sanitized=sanitizeDecimalInput(pastedText); if(sanitized!==undefined&&sanitized!==null)handleQuantity2Change(item.id,sanitized); }} placeholder="0" disabled={!hasUnit2} />
                        </td>
                        <td><input type="text" className="cs-location-input" value={item.location || ''} readOnly disabled title="Location is set in Admin Dashboard" /></td>
                        <td style={{ textAlign: 'center' }}>{isPriority ? (<span className="cs-priority-pill" title="Priority Item - Zero counts require confirmation">Priority</span>) : (<span style={{ color: 'var(--color-text-muted)' }}>-</span>)}</td>
                        <td><input type="text" className="cs-notes-input" value={countData.notes} onChange={(e) => handleItemNotesChange(item.id, e.target.value)} placeholder="Optional notes" maxLength={2000} title="Maximum 2000 characters" /></td>
                      </tr>
                    );
                  })}
                    </tbody>
                  </table>
                  <table className="count-table count-table--tablet">
                    <colgroup>
                      {isSiteLogin ? (
                        <>
                          <col style={{ width: '20%' }} />
                          <col style={{ width: '11%' }} />
                          <col style={{ width: '13%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '28%' }} />
                          <col style={{ width: '14%' }} />
                        </>
                      ) : (
                        <>
                          <col style={{ width: '24%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '14%' }} />
                          <col style={{ width: '34%' }} />
                          <col style={{ width: '14%' }} />
                        </>
                      )}
                    </colgroup>
                    <thead>
                      <tr>
                        <th onClick={() => handleSort('name')} title="Click to sort">Item<span className="cs-sort-indicator">{sortColumn === 'name' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        {isSiteLogin ? (
                          <th onClick={() => handleSort('packSize')} title="Click to sort">Pack Size<span className="cs-sort-indicator">{sortColumn === 'packSize' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        ) : null}
                        <th onClick={() => handleSort('unit')} title="Click to sort">Units<span className="cs-sort-indicator">{sortColumn === 'unit' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('countedQty')} title="Click to sort">Counted qty<span className="cs-sort-indicator">{sortColumn === 'countedQty' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                        <th onClick={() => handleSort('location')} title="Click to sort">Location<span className="cs-sort-indicator">{sortColumn === 'location' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span><span className="cs-tablet-th-sub">Item notes</span></th>
                        <th onClick={() => handleSort('priority')} title="Click to sort">Priority<span className="cs-sort-indicator">{sortColumn === 'priority' ? (sortDirection === 'asc' ? ' ^' : ' v') : ''}</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSortedInventory.map((item) => {
                        const countData = counts[item.id] || { quantity: '', quantity2: '', notes: '' };
                        const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1';
                        const hasUnit2 = item.unit2 && item.unit2.trim() !== '';
                        return (
                          <tr key={item.id}>
                            <td>
                              <div className="cs-td-stack">
                                <span className="cs-item-name">{friendlyItemLabel(item)}</span>
                                <div className="cs-tablet-cat-vendor">
                                  <span className="cs-category-pill">{item.pivotCategory ?? '-'}</span>
                                  {item.vendorItemCode && (
                                    <span className="cs-item-vendor cs-item-vendor--tablet-cat">{item.vendorItemCode}</span>
                                  )}
                                </div>
                              </div>
                            </td>
                            {isSiteLogin ? (
                              <td className="cs-pack-size-cell">{item.packSize || ''}</td>
                            ) : null}
                            <td>
                              <div className="cs-td-stack cs-td-stack--units">
                                <div><span className="cs-td-muted">Unit 1</span> {item.unit1 ?? '-'}</div>
                                <div><span className="cs-td-muted">Unit 2</span> {item.unit2 || '-'}</div>
                              </div>
                            </td>
                            <td>
                              <div className="cs-td-stack cs-td-stack--qty">
                                <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity} onChange={(e) => handleQuantityChange(item.id, e.target.value)} onKeyDown={(e) => { if ([46,8,9,27,13,190,110].indexOf(e.keyCode)!==-1||(e.keyCode===65&&e.ctrlKey===true)||(e.keyCode===67&&e.ctrlKey===true)||(e.keyCode===86&&e.ctrlKey===true)||(e.keyCode===88&&e.ctrlKey===true)||(e.keyCode>=35&&e.keyCode<=39))return; if((e.keyCode>=48&&e.keyCode<=57)||(e.keyCode>=96&&e.keyCode<=105))return; e.preventDefault(); }} onPaste={(e) => { e.preventDefault(); const pastedText=(e.clipboardData||window.clipboardData).getData('text'); const sanitized=sanitizeDecimalInput(pastedText); if(sanitized!==undefined&&sanitized!==null)handleQuantityChange(item.id,sanitized); }} placeholder="0" />
                                <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity2||''} onChange={(e) => handleQuantity2Change(item.id, e.target.value)} onKeyDown={(e) => { if ([46,8,9,27,13,190,110].indexOf(e.keyCode)!==-1||(e.keyCode===65&&e.ctrlKey===true)||(e.keyCode===67&&e.ctrlKey===true)||(e.keyCode===86&&e.ctrlKey===true)||(e.keyCode===88&&e.ctrlKey===true)||(e.keyCode>=35&&e.keyCode<=39))return; if((e.keyCode>=48&&e.keyCode<=57)||(e.keyCode>=96&&e.keyCode<=105))return; e.preventDefault(); }} onPaste={(e) => { e.preventDefault(); const pastedText=(e.clipboardData||window.clipboardData).getData('text'); const sanitized=sanitizeDecimalInput(pastedText); if(sanitized!==undefined&&sanitized!==null)handleQuantity2Change(item.id,sanitized); }} placeholder="0" disabled={!hasUnit2} />
                              </div>
                            </td>
                            <td>
                              <div className="cs-td-stack cs-td-stack--loc-notes">
                                <input type="text" className="cs-location-input" value={item.location || ''} readOnly disabled title="Location is set in Admin Dashboard" />
                                <input type="text" className="cs-notes-input" value={countData.notes} onChange={(e) => handleItemNotesChange(item.id, e.target.value)} placeholder="Optional notes" maxLength={2000} title="Maximum 2000 characters" />
                              </div>
                            </td>
                            <td style={{ textAlign: 'center' }}>{isPriority ? (<span className="cs-priority-pill" title="Priority Item - Zero counts require confirmation">Priority</span>) : (<span style={{ color: 'var(--color-text-muted)' }}>-</span>)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Mobile card view */}
                <div className="cs-mobile-cards">
                  {filteredSortedInventory.map(item => {
                      const countData = counts[item.id] || { quantity: '', quantity2: '', notes: '' };
                      const isPriority = item.priority === true || item.priority === 'true' || item.priority === 1 || item.priority === '1';
                      const hasUnit2 = item.unit2 && item.unit2.trim() !== '';
                      return (
                        <div key={item.id} className="cs-mobile-card">
                          <div className="cs-mobile-card-header">
                            {isSiteLogin ? (
                              <>
                                <div className="cs-mobile-card-header-main">
                                  <div className="cs-mobile-card-name">{friendlyItemLabel(item)}</div>
                                  <div className="cs-mobile-card-pack">{item.packSize || ''}</div>
                                  <div className="cs-mobile-card-meta">
                                    <span className="cs-category-pill">{item.pivotCategory ?? '-'}</span>
                                    {item.vendorItemCode && (
                                      <span className="cs-mobile-card-vendor cs-mobile-card-vendor--inline">{item.vendorItemCode}</span>
                                    )}
                                  </div>
                                </div>
                                <div className="cs-mobile-card-badges">
                                  {isPriority && <span className="cs-priority-pill">Priority</span>}
                                </div>
                              </>
                            ) : (
                              <>
                                <div>
                                  <div className="cs-mobile-card-name">{friendlyItemLabel(item)}</div>
                                  {item.vendorItemCode && <div className="cs-mobile-card-vendor">{item.vendorItemCode}</div>}
                                </div>
                                <div className="cs-mobile-card-badges">
                                  <span className="cs-category-pill">{item.pivotCategory ?? '-'}</span>
                                  {isPriority && <span className="cs-priority-pill">Priority</span>}
                                </div>
                              </>
                            )}
                          </div>
                          <div className="cs-mobile-card-row">
                            <div className="cs-mobile-card-field">
                              <label>{item.unit1 || 'Unit 1'} - Qty</label>
                              <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity} onChange={(e) => handleQuantityChange(item.id, e.target.value)} placeholder="0" />
                            </div>
                            {hasUnit2 && (
                              <div className="cs-mobile-card-field">
                                <label>{item.unit2} - Qty 2</label>
                                <input type="text" inputMode="decimal" className="cs-qty-input" value={countData.quantity2 || ''} onChange={(e) => handleQuantity2Change(item.id, e.target.value)} placeholder="0" />
                              </div>
                            )}
                          </div>
                          <div className="cs-mobile-card-notes">
                            <label>Notes</label>
                            <input type="text" className="cs-notes-input" value={countData.notes} onChange={(e) => handleItemNotesChange(item.id, e.target.value)} placeholder="Optional notes" maxLength={2000} />
                          </div>
                          {item.location && (<div className="cs-mobile-card-location">Location: {item.location}</div>)}
                        </div>
                      );
                    })}
                </div>
              </section>
              </div>
            </div>

            {(error || success || savingDraft || preparingReview) && (
              <div className="cs-draft-save-feedback" role="status" aria-live="polite">
                {preparingReview && (
                  <div className="cs-saving-draft-hint">Preparing review…</div>
                )}
                {savingDraft && !preparingReview && (
                  <div className="cs-saving-draft-hint">Saving draft…</div>
                )}
                {error && renderErrorBanner()}
                {success && <div className="success success-inline">{success}</div>}
              </div>
            )}

            <footer className="bottomBar">
              <button type="button" className="btn btn-secondary" onClick={handleSaveDraft} disabled={savingDraft || preparingReview || !selectedSiteId} title="Save your progress to resume later on any device">
                {savingDraft ? 'Saving...' : 'Save Draft'}
              </button>
              <button type="submit" className="btn btn-primary" ref={reviewCountBtnRef} disabled={!selectedSiteId || !hasEnteredCounts || savingDraft || preparingReview} title="">
                {preparingReview ? 'Preparing review...' : savingDraft ? 'Saving...' : `Review Count (${enteredCountLines} items)`}
              </button>
            </footer>
          </>
        ) : (
          <>
            <h3 className="count-submission-title">Submit Inventory Count</h3>
            {renderPendingFinalSubmissionBanner()}
            {offlineDraft.syncUi && offlineDraft.syncUi.headline && (
              <CountSyncStatusStrip
                syncUi={offlineDraft.syncUi}
                showRetry={Boolean(offlineDraft.syncUi.showRetryButton && offlineDraft.retryLineSync)}
                onRetry={() => offlineDraft.retryLineSync && offlineDraft.retryLineSync()}
                retryBusy={offlineDraft.lineSyncActionBusy}
              />
            )}
            {showFinalSubmissionStatus && (
              <CountSyncStatusStrip syncUi={finalSubmission.submissionUi} className="cs-final-submit-status" />
            )}
            {!isSiteLogin && (
              <div className="form-group">
                <label>Site *</label>
                {sites.length === 0 ? (
                  <div className="error">No sites assigned to you. Please contact your manager.</div>
                ) : (
                  <>
                    <select value={selectedSiteId} onChange={(e) => setSelectedSiteId(e.target.value)} required>
                      <option value="">Select a site</option>
                      {sites.map(site => (<option key={site.id} value={site.id}>{site.name}</option>))}
                    </select>
                    {selectedSiteId && inventory.length === 0 && (<small className="form-hint">No inventory items available for this site's brand</small>)}
                  </>
                )}
              </div>
            )}
            {isSiteLogin && selectedSiteId && (
              <div className="form-group">
                <label>Site</label>
                <input type="text" value={sites.find(s => s.id === selectedSiteId)?.name || ''} disabled className="input-disabled" />
              </div>
            )}
            <div className="form-group">
              <label>Person Counting *</label>
              <input type="text" value={personName} onChange={(e) => setPersonName(e.target.value)} placeholder="Enter the name of the person completing the count" maxLength={200} required className={!personName || personName.trim() === '' ? 'cs-input-error' : ''} />
              <small className="form-hint">Date and time will be automatically recorded when submitted</small>
              {(!personName || personName.trim() === '') && (<small className="form-error">This field is required</small>)}
            </div>
            {(preparingReview || savingDraft) && (
              <div className="cs-saving-draft-hint" role="status" aria-live="polite">
                {preparingReview ? 'Preparing review…' : 'Saving draft…'}
              </div>
            )}
            {error && renderErrorBanner()}
            {success && <div className="success">{success}</div>}
            <div className="form-actions">
              {selectedSiteId && (<button type="button" className="btn btn-secondary" onClick={handleSaveDraft} disabled={savingDraft || preparingReview || !selectedSiteId}>{savingDraft ? 'Saving...' : 'Save Draft'}</button>)}
              <button type="submit" className="btn btn-primary" ref={reviewCountBtnRef} disabled={!selectedSiteId || !hasEnteredCounts || savingDraft || preparingReview} title="">
                {preparingReview ? 'Preparing review...' : savingDraft ? 'Saving...' : `Review Count (${enteredCountLines} items)`}
              </button>
            </div>
          </>
        )}
        {renderConnectionRequiredModal()}
        {renderPendingFinalSubmissionModal()}
        {renderFinalSubmissionSuccessModal()}
      </form>
    </div>
  );
};

export default CountSubmission;